import React, { useEffect, useState } from "react";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import FloatingHelpPanel from "scenes/newproject/SurfacePages/FloatingHelpPanel";
import { getOrganizationHelp, USER_GUIDE_PATH } from "./organizationHelp";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  ExpandLessOutlined,
  ExpandMoreOutlined,
} from "@mui/icons-material";
import apiClient from "../../api/apiClient";
import { fetchWorkspaces, selectWorkspace } from "state/actions/workspaceActions";
import useMessageToast from "hooks/useMessageToast";
import { darkFieldSx } from "utils/darkFieldSx";

const OWNERSHIP_TRANSFER_CONFIRMATION = "transfer ownership";
// Must match ORGANIZATION_DELETE_CONFIRMATION in server/controllers/organizations.js —
// the server rejects anything else, so a drift here would make the dialog unusable.
const ORGANIZATION_DELETE_CONFIRMATION = "delete organization";
const ENTERPRISE_SSO_ENABLED =
  process.env.REACT_APP_ENTERPRISE_SSO_ENABLED !== "false";

const INVITE_CAPABLE_ROLES = ["org_owner", "org_admin"];
const INVITE_ROLE_OPTIONS = [
  { value: "org_admin", label: "Org admin" },
  { value: "member", label: "Member" },
  { value: "viewer", label: "Viewer" },
];
const ORG_ROLE_LABELS = {
  org_owner: "Org owner",
  org_admin: "Org admin",
  member: "Member",
  viewer: "Viewer",
};
const INVITE_STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  revoked: "Revoked",
  expired: "Expired",
};
const BILLING_STATUS_LABELS = {
  unconfigured: "Unconfigured",
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
  suspended: "Suspended",
};
const IDENTITY_PROVIDER_TYPE_LABELS = {
  microsoft_oidc: "Microsoft Entra ID",
  google_oidc: "Google Workspace",
};
const IDENTITY_PROVIDER_TYPE_OPTIONS = [
  { value: "microsoft_oidc", label: "Microsoft Entra ID" },
  { value: "google_oidc", label: "Google Workspace" },
];
const IDENTITY_PROVIDER_STATUS_LABELS = {
  disabled: "Disabled",
  draft: "Draft",
  active: "Active",
};
const DEFAULT_IDENTITY_PROVIDER_SCOPES = ["openid", "profile", "email"];
const IDENTITY_PROVIDER_DEFAULTS = {
  microsoft_oidc: {
    displayName: "Microsoft Entra ID",
    issuer: "https://login.microsoftonline.com/common/v2.0",
    discoveryUrl:
      "https://login.microsoftonline.com/common/v2.0/.well-known/openid-configuration",
    scopes: DEFAULT_IDENTITY_PROVIDER_SCOPES,
  },
  google_oidc: {
    displayName: "Google Workspace",
    issuer: "https://accounts.google.com",
    discoveryUrl: "https://accounts.google.com/.well-known/openid-configuration",
    scopes: DEFAULT_IDENTITY_PROVIDER_SCOPES,
  },
};
const IDENTITY_PROVIDER_VALIDATION_LABELS = {
  not_validated: "Not validated",
  valid: "Valid",
  invalid: "Invalid",
};
const ORG_SUBSCRIPTION_OPTIONS = [
  {
    key: "starter",
    name: "Starter",
    price: "CAD $700/month",
    creditsLabel: "20 plan credits/month",
  },
  {
    key: "professional",
    name: "Professional",
    price: "CAD $1200/month",
    creditsLabel: "40 plan credits/month",
  },
  {
    key: "enterprise",
    name: "Enterprise",
    price: "CAD $2000/month",
    creditsLabel: "80 plan credits/month",
  },
];
const ORG_CREDIT_PACK_OPTIONS = [
  {
    key: "credits_5",
    name: "5 Credits",
    price: "CAD $250",
    creditsLabel: "5 simulation credits",
  },
  {
    key: "credits_20",
    name: "20 Credits",
    price: "CAD $900",
    creditsLabel: "20 simulation credits",
  },
  {
    key: "credits_50",
    name: "50 Credits",
    price: "CAD $2000",
    creditsLabel: "50 simulation credits",
  },
];
const ORG_PLAN_RANK = {
  starter: 1,
  professional: 2,
  enterprise: 3,
};
const MEMBER_ROLE_OPTIONS_BY_ACTOR = {
  org_owner: INVITE_ROLE_OPTIONS,
  org_admin: INVITE_ROLE_OPTIONS.filter((option) => option.value !== "org_admin"),
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

const formatSignedAmount = (value) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return "0";
  return `${numericValue > 0 ? "+" : ""}${numericValue}`;
};

const formatBudgetValue = (value) => {
  if (value === null || typeof value === "undefined") return "No cap";

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "0";
  return String(numericValue);
};

const toBudgetDraftValue = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value);

const buildIdentityProviderDraft = (providerType, provider = null) => {
  const defaults = IDENTITY_PROVIDER_DEFAULTS[providerType] || {};

  return {
    providerType,
    status: provider?.status || "draft",
    enforceSso: Boolean(provider?.enforceSso),
    displayName:
      provider?.displayName || defaults.displayName || IDENTITY_PROVIDER_TYPE_LABELS[providerType] || "",
    issuer: provider?.issuer || defaults.issuer || "",
    clientId: provider?.clientId || "",
    clientSecret: "",
    redirectUris: Array.isArray(provider?.redirectUris)
      ? provider.redirectUris.join("\n")
      : "",
    allowedEmailDomains: Array.isArray(provider?.allowedEmailDomains)
      ? provider.allowedEmailDomains.join(", ")
      : "",
    discoveryUrl: provider?.discoveryUrl || defaults.discoveryUrl || "",
    scopes: Array.isArray(provider?.scopes)
      ? provider.scopes.join(", ")
      : (defaults.scopes || DEFAULT_IDENTITY_PROVIDER_SCOPES).join(", "),
  };
};

const normalizeCommaSeparatedValues = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeLineSeparatedValues = (value) =>
  String(value || "")
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

const isValidIdentityProviderUrl = (value) => {
  try {
    const parsed = new URL(String(value || "").trim());
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
};

const getIdentityProviderTypeUrlValidationError = (providerType, issuer, discoveryUrl) => {
  const rules = {
    microsoft_oidc: {
      allowedHosts: ["login.microsoftonline.com"],
      label: "Microsoft Entra ID",
    },
    google_oidc: {
      allowedHosts: ["accounts.google.com"],
      label: "Google Workspace",
    },
  }[providerType];

  if (!rules) return null;

  const matchesHost = (value) => {
    if (!value) return true;

    try {
      return rules.allowedHosts.includes(new URL(String(value).trim()).hostname.toLowerCase());
    } catch {
      return false;
    }
  };

  if (!matchesHost(issuer)) {
    return {
      field: "issuer",
      message: `${rules.label} issuer URL must use ${rules.allowedHosts.join(", ")}.`,
    };
  }

  if (discoveryUrl && !matchesHost(discoveryUrl)) {
    return {
      field: "discoveryUrl",
      message: `${rules.label} discovery URL must use ${rules.allowedHosts.join(", ")}.`,
    };
  }

  return null;
};

const getIdentityProviderDraftPayload = (draft) => {
  const normalizedScopes = normalizeCommaSeparatedValues(draft?.scopes);

  return {
    status: String(draft?.status || "draft").trim() || "draft",
    enforceSso: Boolean(draft?.enforceSso),
    displayName: String(draft?.displayName || "").trim(),
    issuer: String(draft?.issuer || "").trim(),
    clientId: String(draft?.clientId || "").trim(),
    clientSecret: String(draft?.clientSecret || "").trim(),
    redirectUris: normalizeLineSeparatedValues(draft?.redirectUris),
    allowedEmailDomains: normalizeCommaSeparatedValues(draft?.allowedEmailDomains),
    discoveryUrl: String(draft?.discoveryUrl || "").trim(),
    scopes: normalizedScopes.length ? normalizedScopes : DEFAULT_IDENTITY_PROVIDER_SCOPES,
  };
};

const getIdentityProviderClientValidationErrors = (
  draft,
  { hasExistingSecret = false } = {},
) => {
  const payload = getIdentityProviderDraftPayload(draft);
  const nextErrors = {};

  if (!payload.issuer || !isValidIdentityProviderUrl(payload.issuer)) {
    nextErrors.issuer = "A valid issuer URL is required.";
  }

  if (!payload.clientId) {
    nextErrors.clientId = "Client ID is required.";
  }

  if (
    !payload.redirectUris.length ||
    payload.redirectUris.some((uri) => !isValidIdentityProviderUrl(uri))
  ) {
    nextErrors.redirectUris = "At least one valid redirect URI is required.";
  }

  if (payload.discoveryUrl && !isValidIdentityProviderUrl(payload.discoveryUrl)) {
    nextErrors.discoveryUrl = "Discovery URL must be a valid URL.";
  }

  const providerTypeUrlError = getIdentityProviderTypeUrlValidationError(
    draft?.providerType,
    payload.issuer,
    payload.discoveryUrl,
  );
  if (providerTypeUrlError) {
    nextErrors[providerTypeUrlError.field] = providerTypeUrlError.message;
  }

  if (!payload.clientSecret && !hasExistingSecret) {
    nextErrors.clientSecret = "Client secret is required for a new provider.";
  }

  return nextErrors;
};

const areStringArraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const hasUnsavedIdentityProviderChanges = (draft, provider) => {
  if (!provider) return false;

  const payload = getIdentityProviderDraftPayload(draft);

  if (payload.clientSecret) return true;
  if (payload.status !== String(provider.status || "draft")) return true;
  if (payload.enforceSso !== Boolean(provider.enforceSso)) return true;
  if (payload.displayName !== String(provider.displayName || "").trim()) return true;
  if (payload.issuer !== String(provider.issuer || "").trim()) return true;
  if (payload.clientId !== String(provider.clientId || "").trim()) return true;
  if (!areStringArraysEqual(payload.redirectUris, provider.redirectUris || [])) return true;
  if (
    !areStringArraysEqual(
      payload.allowedEmailDomains,
      Array.isArray(provider.allowedEmailDomains) ? provider.allowedEmailDomains : [],
    )
  ) {
    return true;
  }
  if (payload.discoveryUrl !== String(provider.discoveryUrl || "").trim()) return true;
  if (!areStringArraysEqual(payload.scopes, Array.isArray(provider.scopes) ? provider.scopes : [])) {
    return true;
  }

  return false;
};

const getFirstIdentityProviderError = (errors) => Object.values(errors)[0] || "";

const toDateInputValue = (value) => {
  if (!value) return "";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const buildMemberBudgetDraft = (member) => ({
  limit: toBudgetDraftValue(member?.budget?.limit),
  resetIntervalCount:
    member?.budgetReset?.intervalCount === null ||
    typeof member?.budgetReset?.intervalCount === "undefined"
      ? ""
      : String(member.budgetReset.intervalCount),
  resetIntervalUnit: member?.budgetReset?.intervalUnit || "",
  resetStartAt: toDateInputValue(member?.budgetReset?.startAt),
});

const getMemberBudgetDraft = (draft, member) => draft || buildMemberBudgetDraft(member);

const isMemberBudgetDraftEqual = (draft, member) => {
  const nextDraft = getMemberBudgetDraft(draft, member);
  const currentDraft = buildMemberBudgetDraft(member);

  return (
    nextDraft.limit === currentDraft.limit &&
    nextDraft.resetIntervalCount === currentDraft.resetIntervalCount &&
    nextDraft.resetIntervalUnit === currentDraft.resetIntervalUnit &&
    nextDraft.resetStartAt === currentDraft.resetStartAt
  );
};

const formatBudgetResetFrequency = (budgetReset) => {
  if (!budgetReset?.intervalCount || !budgetReset?.intervalUnit) return "No auto-reset";

  const unitLabel = budgetReset.intervalCount === 1
    ? budgetReset.intervalUnit
    : `${budgetReset.intervalUnit}s`;
  return `Every ${budgetReset.intervalCount} ${unitLabel}`;
};

const getBudgetLimitValue = (budget) => {
  // A limit of 0 is a real cap (blocked — no runs); only null/empty/unset means "no cap".
  const raw = budget?.limit;
  if (raw === null || typeof raw === "undefined" || raw === "") return null;
  const numericValue = Number(raw);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
};

const getBudgetConsumedValue = (budget) => {
  const numericValue = Number(budget?.consumed ?? 0);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
};

const getBudgetRemainingValue = (budget) => {
  const limitValue = getBudgetLimitValue(budget);
  if (limitValue === null) return null;

  const numericValue = Number(budget?.remaining);
  if (Number.isFinite(numericValue) && numericValue >= 0) {
    return numericValue;
  }

  return Math.max(limitValue - getBudgetConsumedValue(budget), 0);
};

const getBudgetUsagePercent = (budget) => {
  const limitValue = getBudgetLimitValue(budget);
  if (limitValue === null) return null;
  return Math.min((getBudgetConsumedValue(budget) / limitValue) * 100, 100);
};

const getBudgetStatus = (budget) => {
  const limitValue = getBudgetLimitValue(budget);
  if (limitValue === null) {
    return { label: "No cap", color: "default" };
  }

  const remainingValue = getBudgetRemainingValue(budget);
  if (remainingValue <= 0) {
    return { label: "At cap", color: "error" };
  }

  if (remainingValue <= Math.max(1, Math.ceil(limitValue * 0.2))) {
    return { label: "Near cap", color: "warning" };
  }

  return { label: "Healthy", color: "success" };
};

const sumBudgetRemaining = (items) =>
  items.reduce((total, item) => total + (getBudgetRemainingValue(item?.budget) ?? 0), 0);

const filterBudgetItems = (items, filter) => {
  if (filter === "near-cap") {
    return items.filter((item) => getBudgetStatus(item?.budget).label === "Near cap");
  }

  if (filter === "at-cap") {
    return items.filter((item) => getBudgetStatus(item?.budget).label === "At cap");
  }

  return items;
};

const formatUsageFundingSource = (value) => {
  const normalized = String(value || "unknown").trim().toLowerCase();
  if (normalized === "plan") return "Plan credits";
  if (normalized === "credit") return "Simulation credits";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatUsageActivityLabel = (value, fallbackLabel) =>
  String(value || "").trim() || fallbackLabel;

const formatMemberBudgetResetSummary = (member) => {
  if (!member?.budgetReset) {
    return "Auto-reset: Off";
  }

  const startAtLabel = formatDateTime(member.budgetReset.startAt);
  const nextResetLabel = formatDateTime(member.budgetReset.nextResetAt);
  return `${formatBudgetResetFrequency(member.budgetReset)} · Starts ${startAtLabel} · Next ${nextResetLabel}`;
};

const buildUsageActivityGroups = (items) => {
  const groups = new Map();

  items.forEach((item) => {
    const fundingSource = String(item?.fundingSource || "unknown").trim() || "unknown";
    const actorUserId = String(item?.actorUserId || "").trim() || "unknown-actor";
    const projectId = String(item?.projectId || "").trim() || "unscoped-project";
    const key = `${fundingSource}:${actorUserId}:${projectId}`;
    const amount = Math.abs(Number(item?.amount ?? 0));
    const createdAt = item?.createdAt ? new Date(item.createdAt) : null;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        fundingSource,
        actorUserId: actorUserId === "unknown-actor" ? null : actorUserId,
        projectId: projectId === "unscoped-project" ? null : projectId,
        actorName: item?.actor?.displayName || item?.actor?.email || null,
        projectName: item?.project?.name || null,
        debitCount: 0,
        totalAmount: 0,
        latestAt: createdAt,
      });
    }

    const current = groups.get(key);
    if (!current.actorName && (item?.actor?.displayName || item?.actor?.email)) {
      current.actorName = item.actor.displayName || item.actor.email;
    }
    if (!current.projectName && item?.project?.name) {
      current.projectName = item.project.name;
    }
    current.debitCount += 1;
    current.totalAmount += Number.isFinite(amount) ? amount : 0;
    if (createdAt && (!current.latestAt || createdAt > current.latestAt)) {
      current.latestAt = createdAt;
    }
  });

  return Array.from(groups.values()).sort((left, right) => {
    const leftTime = left.latestAt ? left.latestAt.getTime() : 0;
    const rightTime = right.latestAt ? right.latestAt.getTime() : 0;
    return rightTime - leftTime;
  });
};

const getBillingStatusChipColor = (status) => {
  if (status === "active") return "success";
  if (status === "past_due") return "warning";
  if (status === "canceled" || status === "suspended") return "error";
  return "default";
};

const actionButtonSx = {
  minHeight: 42,
  fontWeight: 700,
  letterSpacing: 0.2,
  color: "#04111f",
  "&.Mui-disabled": {
    backgroundColor: "rgba(154, 170, 196, 0.22)",
    color: "rgba(241, 246, 255, 0.62)",
    borderColor: "rgba(255, 255, 255, 0.1)",
  },
};

const outlinedActionButtonSx = {
  ...actionButtonSx,
  borderColor: "rgba(0, 230, 118, 0.55)",
  color: "#d8ffea",
  "&:hover": {
    borderColor: "#00e676",
    backgroundColor: "rgba(0, 230, 118, 0.08)",
  },
  "&.Mui-disabled": {
    backgroundColor: "transparent",
    color: "rgba(241, 246, 255, 0.62)",
    borderColor: "rgba(255, 255, 255, 0.1)",
  },
};

const ownershipActionSlotSx = {
  width: { xs: "100%", sm: 210 },
  minWidth: { xs: "100%", sm: 210 },
  minHeight: 42,
};

// Resting-state only historically, so focus still fell back to the theme's blue and
// vanished on the dark dialog. Extends the shared dark-field style with this tab's
// inset field background.
const darkInputSx = {
  ...darkFieldSx,
  "& .MuiOutlinedInput-root": {
    ...darkFieldSx["& .MuiOutlinedInput-root"],
    backgroundColor: "rgba(11, 18, 48, 0.72)",
  },
};

const organizationsPageHeadingSx = {
  color: "#f7fbff",
  fontSize: { xs: "1.08rem", sm: "1.2rem" },
  fontWeight: 800,
  letterSpacing: "0.01em",
};

const organizationCardTitleSx = {
  color: "#f8fbff",
  fontSize: { xs: "0.98rem", sm: "1.04rem" },
  fontWeight: 750,
  letterSpacing: "0.01em",
};

const billingTabHeadingSx = {
  color: "#f8fbff",
  fontSize: { xs: "1rem", sm: "1.08rem" },
  fontWeight: 800,
  letterSpacing: "0.01em",
};

const billingSectionHeadingSx = {
  color: "#f7fbff",
  fontSize: { xs: "0.88rem", sm: "0.92rem" },
  fontWeight: 780,
  letterSpacing: "0.01em",
};

const billingSectionContainerSx = {
  display: "grid",
  gap: 1.25,
  border: "1px solid rgba(255, 255, 255, 0.12)",
  borderRadius: 2,
  px: { xs: 1.25, sm: 1.5 },
  py: { xs: 1.25, sm: 1.5 },
  backgroundColor: "rgba(255, 255, 255, 0.03)",
};

const budgetCardInputSx = {
  "& .MuiInputLabel-root": {
    color: "rgba(232, 238, 255, 0.84)",
  },
  "& .MuiOutlinedInput-root": {
    color: "#f8fbff",
    backgroundColor: "rgba(173, 186, 228, 0.18)",
  },
  "& input::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
    opacity: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(210, 221, 255, 0.32)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(225, 234, 255, 0.5)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(236, 242, 255, 0.72)",
  },
};

const lightPanelInputSx = {
  "& .MuiInputLabel-root": {
    color: "rgba(232, 238, 255, 0.84)",
  },
  "& .MuiOutlinedInput-root": {
    color: "#f8fbff",
    backgroundColor: "rgba(173, 186, 228, 0.18)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(210, 221, 255, 0.32)",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(225, 234, 255, 0.5)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(236, 242, 255, 0.72)",
  },
  "& .MuiSvgIcon-root": {
    color: "rgba(232, 238, 255, 0.88)",
  },
};

const secondaryRowActionButtonSx = {
  minHeight: 32,
  px: 1.2,
  py: 0.45,
  fontSize: "0.74rem",
  fontWeight: 700,
  borderRadius: 1.4,
  color: "#d4defd",
  borderColor: "rgba(191, 205, 255, 0.22)",
  backgroundColor: "rgba(164, 180, 230, 0.08)",
  "&:hover": {
    borderColor: "rgba(214, 224, 255, 0.34)",
    backgroundColor: "rgba(164, 180, 230, 0.16)",
  },
};

const secondaryRowDangerButtonSx = {
  ...secondaryRowActionButtonSx,
  color: "#ffcdc8",
  borderColor: "rgba(255, 146, 130, 0.24)",
  backgroundColor: "rgba(255, 120, 96, 0.08)",
  "&:hover": {
    borderColor: "rgba(255, 170, 158, 0.38)",
    backgroundColor: "rgba(255, 120, 96, 0.16)",
  },
};

const organizationCollapseToggleSx = {
  color: "#fff2cf",
  backgroundColor: "rgba(191, 137, 24, 0.42)",
  border: "1px solid rgba(255, 210, 126, 0.42)",
  borderRadius: 1.4,
  "& .MuiSvgIcon-root": {
    color: "#fff2cf",
  },
  "&:hover": {
    backgroundColor: "rgba(191, 137, 24, 0.56)",
  },
};

const sectionCollapseToggleSx = {
  color: "#d7f6ff",
  backgroundColor: "rgba(24, 118, 191, 0.42)",
  border: "1px solid rgba(114, 198, 255, 0.36)",
  borderRadius: 1.4,
  "& .MuiSvgIcon-root": {
    color: "#d7f6ff",
  },
  "&:hover": {
    backgroundColor: "rgba(24, 118, 191, 0.56)",
  },
};

const nestedCollapseToggleSx = {
  color: "#f1e7ff",
  backgroundColor: "rgba(122, 81, 221, 0.42)",
  border: "1px solid rgba(190, 164, 255, 0.34)",
  borderRadius: 1.4,
  "& .MuiSvgIcon-root": {
    color: "#f1e7ff",
  },
  "&:hover": {
    backgroundColor: "rgba(122, 81, 221, 0.56)",
  },
};

const getOrganizationSsoProviderMetadata = (providerType) => {
  if (providerType === "google_oidc") {
    return {
      providerKey: "google",
      providerLabel: "Google Workspace",
      successParam: "ssoGoogle",
      errorParam: "ssoGoogleError",
    };
  }

  return {
    providerKey: "microsoft",
    providerLabel: "Microsoft",
    successParam: "ssoMicrosoft",
    errorParam: "ssoMicrosoftError",
  };
};

const getOrganizationRedirectAction = (search) => {
  const params = new URLSearchParams(search || "");
  const organizationId = String(params.get("organizationId") || "").trim();

  if (params.get("ssoGoogle") === "1" && organizationId) {
    return { organizationId, type: "sso-google-success", providerType: "google_oidc" };
  }

  if (params.get("ssoGoogleError")) {
    return {
      organizationId: organizationId || null,
      type: "sso-google-error",
      errorCode: String(params.get("ssoGoogleError") || "").trim(),
      providerType: "google_oidc",
    };
  }

  if (params.get("ssoMicrosoft") === "1" && organizationId) {
    return { organizationId, type: "sso-microsoft-success", providerType: "microsoft_oidc" };
  }

  if (params.get("ssoMicrosoftError")) {
    return {
      organizationId: organizationId || null,
      type: "sso-microsoft-error",
      errorCode: String(params.get("ssoMicrosoftError") || "").trim(),
      providerType: "microsoft_oidc",
    };
  }

  if (!organizationId) return null;

  if (params.get("orgBillingSetup") === "1") {
    return { organizationId, type: "billing-setup-success" };
  }

  if (params.get("orgBillingCanceled") === "1") {
    return { organizationId, type: "billing-setup-canceled" };
  }

  if (params.get("orgCreditPurchase") === "1") {
    return { organizationId, type: "credit-purchase-success" };
  }

  if (params.get("orgCreditPurchaseCanceled") === "1") {
    return { organizationId, type: "credit-purchase-canceled" };
  }

  return null;
};

const clearOrganizationRedirectParams = (search) => {
  const params = new URLSearchParams(search || "");
  params.delete("ssoGoogle");
  params.delete("ssoGoogleError");
  params.delete("ssoMicrosoft");
  params.delete("ssoMicrosoftError");
  params.delete("orgBillingSetup");
  params.delete("orgBillingCanceled");
  params.delete("orgCreditPurchase");
  params.delete("orgCreditPurchaseCanceled");
  params.delete("organizationId");
  params.set("tab", "organizations");
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

const getOrganizationSsoRedirectMessage = (providerType, errorCode) => {
  const { providerLabel } = getOrganizationSsoProviderMetadata(providerType);

  if (errorCode === "invalid_state") {
    return `${providerLabel} sign-in could not be completed because the login state expired or did not match.`;
  }

  if (errorCode === "invalid_token") {
    return `${providerLabel} sign-in could not verify the returned identity token.`;
  }

  if (errorCode === "provider_inactive") {
    return `${providerLabel} sign-in is not active for this organization.`;
  }

  if (errorCode === "access_denied") {
    return `Your CDT account is not eligible to use ${providerLabel} sign-in for this organization.`;
  }

  if (errorCode === "account_required") {
    return `A verified CDT account is required before using ${providerLabel} organization sign-in.`;
  }

  return `${providerLabel} sign-in could not be completed.`;
};

// Shape the org billing-profile form draft from the billing summary (or empty).
const billingDraftFromData = (billing) => ({
  billingEmail: billing?.billingEmail || "",
  billingName: billing?.billingName || "",
  billingCurrency: billing?.billingCurrency || "cad",
  billingAddress: {
    line1: billing?.billingAddress?.line1 || "",
    line2: billing?.billingAddress?.line2 || "",
    city: billing?.billingAddress?.city || "",
    state: billing?.billingAddress?.state || "",
    postalCode: billing?.billingAddress?.postalCode || "",
    country: billing?.billingAddress?.country || "",
  },
  taxIdType: billing?.taxId?.type || "",
  taxIdValue: billing?.taxId?.value || "",
});

const OrganizationsTab = () => {
  const currentUser = useSelector((state) => state.auth.user);
  // Needed when deleting an organization: if it is the workspace currently being viewed,
  // the app has to be moved back to Personal or it keeps rendering a workspace that no
  // longer exists.
  const selectedWorkspaceKey = useSelector(
    (state) => state.workspace.selectedWorkspaceKey,
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [membersByOrganizationId, setMembersByOrganizationId] = useState({});
  const [membersLoadingByOrganizationId, setMembersLoadingByOrganizationId] = useState({});
  const [invitesByOrganizationId, setInvitesByOrganizationId] = useState({});
  const [invitesLoadingByOrganizationId, setInvitesLoadingByOrganizationId] = useState({});
  const [billingByOrganizationId, setBillingByOrganizationId] = useState({});
  const [organizationInvoices, setOrganizationInvoices] = useState({});
  const [organizationInvoicesLoading, setOrganizationInvoicesLoading] = useState({});
  const [billingLoadingByOrganizationId, setBillingLoadingByOrganizationId] = useState({});
  const [billingLedgerByOrganizationId, setBillingLedgerByOrganizationId] = useState({});
  const [billingLedgerLoadingByOrganizationId, setBillingLedgerLoadingByOrganizationId] =
    useState({});
  const [billingCyclesByOrganizationId, setBillingCyclesByOrganizationId] = useState({});
  const [billingCyclesLoadingByOrganizationId, setBillingCyclesLoadingByOrganizationId] =
    useState({});
  const [billingBudgetsByOrganizationId, setBillingBudgetsByOrganizationId] = useState({});
  const [billingBudgetsLoadingByOrganizationId, setBillingBudgetsLoadingByOrganizationId] =
    useState({});
  const [paymentMethodsByOrganizationId, setPaymentMethodsByOrganizationId] = useState({});
  const [paymentMethodsLoadingByOrganizationId, setPaymentMethodsLoadingByOrganizationId] =
    useState({});
  const [identityProvidersByOrganizationId, setIdentityProvidersByOrganizationId] = useState({});
  const [identityProvidersLoadingByOrganizationId, setIdentityProvidersLoadingByOrganizationId] =
    useState({});
  const [identityProviderDraftsByOrganizationId, setIdentityProviderDraftsByOrganizationId] =
    useState({});
  const [selectedIdentityProviderTypeByOrganizationId, setSelectedIdentityProviderTypeByOrganizationId] =
    useState({});
  const [identityProviderValidationStateByOrganizationId, setIdentityProviderValidationStateByOrganizationId] =
    useState({});
  const [billingProfileDrafts, setBillingProfileDrafts] = useState({});
  const [trialPromoCodesByOrganizationId, setTrialPromoCodesByOrganizationId] = useState({});
  const [trialMessagesByOrganizationId, setTrialMessagesByOrganizationId] = useState({});
  const [trialInputsOpenByOrganizationId, setTrialInputsOpenByOrganizationId] = useState({});
  const [memberRoleDrafts, setMemberRoleDrafts] = useState({});
  const [memberBudgetDrafts, setMemberBudgetDrafts] = useState({});
  const [projectBudgetDrafts, setProjectBudgetDrafts] = useState({});
  const [memberDashboardFilters, setMemberDashboardFilters] = useState({});
  const [projectDashboardFilters, setProjectDashboardFilters] = useState({});
  const [organizationBillingTabs, setOrganizationBillingTabs] = useState({});
  const [usageBudgetSectionExpansionByOrganizationId, setUsageBudgetSectionExpansionByOrganizationId] = useState({});
  const [expandedOrganizations, setExpandedOrganizations] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [billingSavingOrgId, setBillingSavingOrgId] = useState("");
  const [billingActionKey, setBillingActionKey] = useState("");
  const [inviteSubmittingOrgId, setInviteSubmittingOrgId] = useState("");
  const [inviteActionKey, setInviteActionKey] = useState("");
  const [memberActionKey, setMemberActionKey] = useState("");
  const [inviteEmails, setInviteEmails] = useState({});
  const [inviteRoles, setInviteRoles] = useState({});
  const [transferDialog, setTransferDialog] = useState(null);
  const [transferConfirmationText, setTransferConfirmationText] = useState("");
  const [deleteOrgDialog, setDeleteOrgDialog] = useState(null);
  const [deleteOrgConfirmationText, setDeleteOrgConfirmationText] = useState("");
  const [deletingOrganizationId, setDeletingOrganizationId] = useState("");
  const [deleteOrgBlocker, setDeleteOrgBlocker] = useState("");
  const [organizationSectionTabs, setOrganizationSectionTabs] = useState({});
  // Contextual help: which topic key the floating panel is showing (null = closed).
  const [helpTopicKey, setHelpTopicKey] = useState(null);
  const helpTopic = getOrganizationHelp(helpTopicKey);
  const [mfaRequiredForOrgAdmin, setMfaRequiredForOrgAdmin] = useState(false);
  const [error, setError] = useState("");
  useMessageToast(error, "error");
  const [success, setSuccess] = useState("");

  useMessageToast(success, "success");
  const toggleOrganizationExpanded = (organizationId) => {
    setExpandedOrganizations((current) => ({
      ...current,
      [organizationId]: !current[organizationId],
    }));
  };

  const isOrganizationSectionExpanded = (
    organizationId,
    sectionKey,
    defaultExpanded = false,
  ) => {
    const currentValue = usageBudgetSectionExpansionByOrganizationId[organizationId]?.[sectionKey];
    if (typeof currentValue === "undefined") {
      return defaultExpanded;
    }

    return Boolean(currentValue);
  };

  const toggleOrganizationSectionExpanded = (
    organizationId,
    sectionKey,
    defaultExpanded = false,
  ) => {
    setUsageBudgetSectionExpansionByOrganizationId((current) => {
      const currentValue = current[organizationId]?.[sectionKey];
      const resolvedValue =
        typeof currentValue === "undefined" ? defaultExpanded : Boolean(currentValue);

      return {
        ...current,
        [organizationId]: {
          ...(current[organizationId] || {}),
          [sectionKey]: !resolvedValue,
        },
      };
    });
  };

  const isUsageBudgetSectionExpanded = (organizationId, sectionKey) =>
    isOrganizationSectionExpanded(organizationId, sectionKey);

  const toggleUsageBudgetSectionExpanded = (organizationId, sectionKey) => {
    toggleOrganizationSectionExpanded(organizationId, sectionKey);
  };

  const getRequestErrorDetails = (requestError, fallbackMessage) => ({
    code: requestError?.response?.data?.code || "",
    error:
      requestError?.response?.data?.message ||
      fallbackMessage,
  });

  const clearRequestFeedback = () => {
    setMfaRequiredForOrgAdmin(false);
    setError("");
  };

  const applyRequestError = (requestError, fallbackMessage) => {
    const details = getRequestErrorDetails(requestError, fallbackMessage);
    setMfaRequiredForOrgAdmin(details.code === "ORG_MFA_REQUIRED");
    setError(details.error);
    return details.error;
  };

  // Shared "?" trigger, so inline section headings and the org tab bar get the same control
  // as the collapsible sections (which take it via renderOrganizationCollapsibleSection).
  // Renders nothing when the key has no topic, so a button can never open an empty panel.
  const renderHelpButton = (topicKey, label) => {
    if (!getOrganizationHelp(topicKey)) return null;
    return (
      <IconButton
        aria-label={`What is ${label.toLowerCase()}?`}
        size="small"
        onClick={() => setHelpTopicKey(topicKey)}
        sx={{ color: "rgba(223, 229, 255, 0.62)", "&:hover": { color: "#fff" } }}
      >
        <HelpOutlineOutlinedIcon fontSize="small" />
      </IconButton>
    );
  };

  const renderOrganizationCollapsibleSection = ({
    organizationId,
    organizationName,
    sectionKey,
    title,
    description,
    children,
    action = null,
    actionBelow = null,
    defaultExpanded = false,
    contentSx = {},
    toggleSx = sectionCollapseToggleSx,
  }) => {
    const isExpanded = isOrganizationSectionExpanded(
      organizationId,
      sectionKey,
      defaultExpanded,
    );

    return (
      <Box
        sx={{
          ...billingSectionContainerSx,
          px: 0,
          py: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.25, sm: 1.5 },
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="body2" sx={billingSectionHeadingSx}>
              {title}
            </Typography>
            {description ? (
              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {actionBelow ? null : action}
            {getOrganizationHelp(sectionKey) ? (
              <IconButton
                aria-label={`What is ${title.toLowerCase()}?`}
                size="small"
                onClick={() => setHelpTopicKey(sectionKey)}
                sx={{ color: "rgba(223, 229, 255, 0.62)", "&:hover": { color: "#fff" } }}
              >
                <HelpOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            ) : null}
            <IconButton
              aria-label={
                isExpanded
                  ? `Collapse ${title.toLowerCase()} for ${organizationName}`
                  : `Expand ${title.toLowerCase()} for ${organizationName}`
              }
              onClick={() =>
                toggleOrganizationSectionExpanded(
                  organizationId,
                  sectionKey,
                  defaultExpanded,
                )
              }
              size="small"
              sx={toggleSx}
            >
              {isExpanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
            </IconButton>
          </Stack>
        </Box>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: { xs: 1.25, sm: 1.5 }, pb: { xs: 1.25, sm: 1.5 }, ...contentSx }}>
            {actionBelow ? <Box sx={{ mb: 1.25 }}>{actionBelow}</Box> : null}
            {children}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const handleMfaRequiredNavigation = () => {
    navigate(
      {
        pathname: location.pathname,
        search: "?tab=security",
      },
      { replace: false },
    );
  };

  const getAvailableOrganizationTabs = (organization) => {
    if (organization.membership?.orgRole === "org_owner") {
      return ENTERPRISE_SSO_ENABLED
        ? ["billing", "identity", "invites", "members"]
        : ["billing", "invites", "members"];
    }

    if (INVITE_CAPABLE_ROLES.includes(organization.membership?.orgRole)) {
      return ["invites", "members"];
    }

    return [];
  };

  const getOrganizationTabValue = (organization) => {
    const availableTabs = getAvailableOrganizationTabs(organization);
    if (!availableTabs.length) return null;

    const savedTab = organizationSectionTabs[organization._id];
    return availableTabs.includes(savedTab) ? savedTab : availableTabs[0];
  };

  const handleOrganizationTabChange = (organizationId, nextTab) => {
    setOrganizationSectionTabs((current) => ({
      ...current,
      [organizationId]: nextTab,
    }));
  };

  const getOrganizationBillingTabValue = (organizationId) =>
    organizationBillingTabs[organizationId] || "billing";

  const getSelectedIdentityProviderType = (organizationId, providers = []) => {
    const savedType = selectedIdentityProviderTypeByOrganizationId[organizationId];
    if (IDENTITY_PROVIDER_TYPE_OPTIONS.some((option) => option.value === savedType)) {
      return savedType;
    }

    return providers[0]?.providerType || "microsoft_oidc";
  };

  const getIdentityProviderDraft = (organizationId, providerType, providers = []) => {
    const existingDraft = identityProviderDraftsByOrganizationId[organizationId]?.[providerType];
    if (existingDraft) return existingDraft;

    const provider = providers.find((item) => item.providerType === providerType) || null;
    return buildIdentityProviderDraft(providerType, provider);
  };

  const getIdentityProviderValidationState = (organizationId, providerType) =>
    identityProviderValidationStateByOrganizationId[organizationId]?.[providerType] || {
      touchedFields: {},
      submitAttempted: false,
    };

  const handleOrganizationBillingTabChange = (organizationId, nextTab) => {
    setOrganizationBillingTabs((current) => ({
      ...current,
      [organizationId]: nextTab,
    }));
  };

  const handleIdentityProviderTypeChange = (organizationId, providerType) => {
    setSelectedIdentityProviderTypeByOrganizationId((current) => ({
      ...current,
      [organizationId]: providerType,
    }));

    setIdentityProviderValidationStateByOrganizationId((current) => ({
      ...current,
      [organizationId]: {
        ...(current[organizationId] || {}),
        [providerType]: {
          touchedFields: {},
          submitAttempted: false,
        },
      },
    }));

    setIdentityProviderDraftsByOrganizationId((current) => ({
      ...current,
      [organizationId]: {
        ...(current[organizationId] || {}),
        [providerType]: buildIdentityProviderDraft(
          providerType,
          (identityProvidersByOrganizationId[organizationId] || []).find(
            (item) => item.providerType === providerType,
          ) || null,
        ),
      },
    }));
  };

  const handleIdentityProviderDraftChange = (organizationId, providerType, field, value) => {
    setIdentityProviderValidationStateByOrganizationId((current) => ({
      ...current,
      [organizationId]: {
        ...(current[organizationId] || {}),
        [providerType]: {
          touchedFields: {
            ...((current[organizationId] || {})[providerType]?.touchedFields || {}),
            [field]: true,
          },
          submitAttempted: (current[organizationId] || {})[providerType]?.submitAttempted || false,
        },
      },
    }));

    setIdentityProviderDraftsByOrganizationId((current) => ({
      ...current,
      [organizationId]: {
        ...(current[organizationId] || {}),
        [providerType]: {
          ...getIdentityProviderDraft(
            organizationId,
            providerType,
            identityProvidersByOrganizationId[organizationId] || [],
          ),
          ...((current[organizationId] || {})[providerType] || {}),
          [field]: value,
        },
      },
    }));
  };

  const getPendingInviteCount = (organizationId) =>
    (invitesByOrganizationId[organizationId] || []).filter(
      (invite) => String(invite.status || "pending") === "pending",
    ).length;

  const loadBillingSummaries = async (organizationIds) => {
    if (!organizationIds.length) return;

    setBillingLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/billing/summary`,
            { withCredentials: true },
          );

          return {
            organizationId,
            billing: data?.billing || null,
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load organization billing summary.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error && result.billing);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setBillingByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, billing }) => {
          next[organizationId] = billing;
        });
        return next;
      });

      setBillingProfileDrafts((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, billing }) => {
          next[organizationId] = billingDraftFromData(billing);
        });
        return next;
      });
    }

    setBillingLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  const loadBillingLedger = async (organizationIds) => {
    if (!organizationIds.length) return;

    setBillingLedgerLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/billing/ledger`,
            { withCredentials: true },
          );

          return {
            organizationId,
            items: Array.isArray(data?.items) ? data.items : [],
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load organization billing ledger.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setBillingLedgerByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, items }) => {
          next[organizationId] = items;
        });
        return next;
      });
    }

    setBillingLedgerLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  const loadBillingCycles = async (organizationIds) => {
    if (!organizationIds.length) return;

    setBillingCyclesLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/billing/cycles`,
            { withCredentials: true },
          );

          return {
            organizationId,
            items: Array.isArray(data?.items) ? data.items : [],
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load organization plan credit cycles.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setBillingCyclesByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, items }) => {
          next[organizationId] = items;
        });
        return next;
      });
    }

    setBillingCyclesLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  const loadBillingBudgets = async (organizationIds) => {
    if (!organizationIds.length) return;

    setBillingBudgetsLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/billing/budgets`,
            { withCredentials: true },
          );

          return {
            organizationId,
            members: Array.isArray(data?.members) ? data.members : [],
            projects: Array.isArray(data?.projects) ? data.projects : [],
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load organization billing budgets.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setBillingBudgetsByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, members, projects }) => {
          next[organizationId] = { members, projects };
        });
        return next;
      });

      setMemberBudgetDrafts((current) => {
        const next = { ...current };
        successfulResults.forEach(({ members }) => {
          members.forEach((member) => {
            next[member._id] = buildMemberBudgetDraft(member);
          });
        });
        return next;
      });

      setProjectBudgetDrafts((current) => {
        const next = { ...current };
        successfulResults.forEach(({ projects }) => {
          projects.forEach((project) => {
            next[project._id] = toBudgetDraftValue(project?.budget?.limit);
          });
        });
        return next;
      });
    }

    setBillingBudgetsLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  const loadOrganizationIdentityProviders = async (organizationIds) => {
    if (!ENTERPRISE_SSO_ENABLED || !organizationIds.length) return;

    setIdentityProvidersLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/identity-providers`,
            { withCredentials: true },
          );

          return {
            organizationId,
            providers: Array.isArray(data?.providers) ? data.providers : [],
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load identity provider configuration.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setIdentityProvidersByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, providers }) => {
          next[organizationId] = providers;
        });
        return next;
      });

      setIdentityProviderDraftsByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, providers }) => {
          const existingOrgDrafts = next[organizationId] || {};
          const mergedDrafts = { ...existingOrgDrafts };

          providers.forEach((provider) => {
            const previousDraft = existingOrgDrafts[provider.providerType] || {};
            mergedDrafts[provider.providerType] = {
              ...buildIdentityProviderDraft(provider.providerType, provider),
              ...previousDraft,
              clientSecret: "",
            };
          });

          next[organizationId] = mergedDrafts;
        });
        return next;
      });
    }

    setIdentityProvidersLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  const loadOrganizationInvoices = async (organizationIds) => {
    if (!Array.isArray(organizationIds) || !organizationIds.length) return;
    setOrganizationInvoicesLoading((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });
    await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/billing/invoices`,
            { withCredentials: true },
          );
          setOrganizationInvoices((current) => ({
            ...current,
            [organizationId]: Array.isArray(data?.invoices) ? data.invoices : [],
          }));
        } catch (requestError) {
          // Non-fatal; keep any previously loaded value.
        } finally {
          setOrganizationInvoicesLoading((current) => ({
            ...current,
            [organizationId]: false,
          }));
        }
      }),
    );
  };

  const loadOrganizationPaymentMethods = async (organizationIds) => {
    if (!organizationIds.length) return;

    setPaymentMethodsLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/billing/payment-methods`,
            { withCredentials: true },
          );

          return {
            organizationId,
            methods: Array.isArray(data) ? data : [],
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load organization payment methods.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setPaymentMethodsByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, methods }) => {
          next[organizationId] = methods;
        });
        return next;
      });
    }

    setPaymentMethodsLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  const refreshOrganizationBilling = async (organizationId) => {
    await Promise.all([
      loadBillingSummaries([organizationId]),
      loadBillingLedger([organizationId]),
      loadBillingCycles([organizationId]),
      loadBillingBudgets([organizationId]),
      loadOrganizationPaymentMethods([organizationId]),
      loadOrganizationInvoices([organizationId]),
    ]);
  };

  const refreshOrganizationIdentityProviders = async (organizationId) => {
    await loadOrganizationIdentityProviders([organizationId]);
  };

  const handleSaveIdentityProvider = async (organization) => {
    const providers = identityProvidersByOrganizationId[organization._id] || [];
    const providerType = getSelectedIdentityProviderType(organization._id, providers);
    const existingProvider =
      providers.find((provider) => provider.providerType === providerType) || null;
    const draft = getIdentityProviderDraft(organization._id, providerType, providers);
    const clientErrors = getIdentityProviderClientValidationErrors(draft, {
      hasExistingSecret: Boolean(existingProvider?.hasClientSecret),
    });
    const actionKey = `${organization._id}:identity-save:${providerType}`;

    setIdentityProviderValidationStateByOrganizationId((current) => ({
      ...current,
      [organization._id]: {
        ...(current[organization._id] || {}),
        [providerType]: {
          ...(current[organization._id]?.[providerType] || {}),
          touchedFields: {
            ...((current[organization._id]?.[providerType] || {}).touchedFields || {}),
            issuer: true,
            clientId: true,
            clientSecret: true,
            redirectUris: true,
            discoveryUrl: true,
          },
          submitAttempted: true,
        },
      },
    }));

    if (Object.keys(clientErrors).length) {
      setError(getFirstIdentityProviderError(clientErrors));
      setSuccess("");
      return;
    }

    clearRequestFeedback();
    setSuccess("");
    setBillingActionKey(actionKey);

    const payload = getIdentityProviderDraftPayload(draft);

    try {
      const { data } = await apiClient.put(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/identity-providers/${providerType}`,
        {
          status: payload.status,
          enforceSso: payload.enforceSso,
          displayName: payload.displayName,
          issuer: payload.issuer,
          clientId: payload.clientId,
          clientSecret: payload.clientSecret,
          redirectUris: payload.redirectUris,
          allowedEmailDomains: payload.allowedEmailDomains,
          discoveryUrl: payload.discoveryUrl,
          scopes: payload.scopes,
        },
        { withCredentials: true },
      );

      await refreshOrganizationIdentityProviders(organization._id);
      setSuccess(
        data?.message || `Saved identity provider configuration for ${organization.name}.`,
      );
      setIdentityProviderDraftsByOrganizationId((current) => ({
        ...current,
        [organization._id]: {
          ...(current[organization._id] || {}),
          [providerType]: {
            ...((current[organization._id] || {})[providerType] || {}),
            clientSecret: "",
          },
        },
      }));
      setIdentityProviderValidationStateByOrganizationId((current) => ({
        ...current,
        [organization._id]: {
          ...(current[organization._id] || {}),
          [providerType]: {
            touchedFields: {},
            submitAttempted: false,
          },
        },
      }));
    } catch (requestError) {
      applyRequestError(requestError, "Failed to save identity provider configuration.");
    } finally {
      setBillingActionKey("");
    }
  };

  const handleValidateIdentityProvider = async (organization) => {
    const providers = identityProvidersByOrganizationId[organization._id] || [];
    const providerType = getSelectedIdentityProviderType(organization._id, providers);
    const selectedProvider =
      providers.find((provider) => provider.providerType === providerType) || null;
    const draft = getIdentityProviderDraft(organization._id, providerType, providers);
    const clientErrors = getIdentityProviderClientValidationErrors(draft, {
      hasExistingSecret: Boolean(selectedProvider?.hasClientSecret),
    });
    const hasUnsavedChanges = hasUnsavedIdentityProviderChanges(draft, selectedProvider);
    const actionKey = `${organization._id}:identity-validate:${providerType}`;

    if (!selectedProvider) {
      setError("Save the identity provider configuration before running validation.");
      setSuccess("");
      return;
    }

    setIdentityProviderValidationStateByOrganizationId((current) => ({
      ...current,
      [organization._id]: {
        ...(current[organization._id] || {}),
        [providerType]: {
          ...(current[organization._id]?.[providerType] || {}),
          touchedFields: {
            ...((current[organization._id]?.[providerType] || {}).touchedFields || {}),
            issuer: true,
            clientId: true,
            clientSecret: true,
            redirectUris: true,
            discoveryUrl: true,
          },
          submitAttempted: true,
        },
      },
    }));

    if (Object.keys(clientErrors).length) {
      setError(getFirstIdentityProviderError(clientErrors));
      setSuccess("");
      return;
    }

    if (hasUnsavedChanges) {
      setError("Save identity provider changes before running validation.");
      setSuccess("");
      return;
    }

    clearRequestFeedback();
    setSuccess("");
    setBillingActionKey(actionKey);

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/identity-providers/${providerType}/validate`,
        {},
        { withCredentials: true },
      );

      await refreshOrganizationIdentityProviders(organization._id);
      setSuccess(
        data?.message || `Validated identity provider configuration for ${organization.name}.`,
      );
    } catch (requestError) {
      applyRequestError(requestError, "Failed to validate identity provider configuration.");
      await refreshOrganizationIdentityProviders(organization._id);
    } finally {
      setBillingActionKey("");
    }
  };

  const handleStartOrganizationSsoLogin = (organization, providerType) => {
    const { providerKey, providerLabel } = getOrganizationSsoProviderMetadata(providerType);
    const providers = identityProvidersByOrganizationId[organization._id] || [];
    const provider = providers.find((item) => item.providerType === providerType) || null;

    if (!provider || provider.status !== "active") {
      setError(`${providerLabel} sign-in can only be launched after the provider is active.`);
      setSuccess("");
      return;
    }

    const startUrl = `${process.env.REACT_APP_BASE_URL}/user/sso/${providerKey}/start?organizationId=${encodeURIComponent(
      organization._id,
    )}`;

    setError("");
    setSuccess("");
    window.location.assign(startUrl);
  };

  const loadMembers = async (organizationIds) => {
    if (!organizationIds.length) return;

    setMembersLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/members`,
            { withCredentials: true },
          );

          return {
            organizationId,
            members: Array.isArray(data?.members) ? data.members : [],
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load organization members.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setMembersByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, members }) => {
          next[organizationId] = members;
        });
        return next;
      });
      setMemberRoleDrafts((current) => {
        const next = { ...current };
        successfulResults.forEach(({ members }) => {
          members.forEach((member) => {
            next[member._id] = member.orgRole;
          });
        });
        return next;
      });
    }

    setMembersLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  const loadInvites = async (organizationIds) => {
    if (!organizationIds.length) return;

    setInvitesLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = true;
      });
      return next;
    });

    const results = await Promise.all(
      organizationIds.map(async (organizationId) => {
        try {
          const { data } = await apiClient.get(
            `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}/invites`,
            { withCredentials: true },
          );

          return {
            organizationId,
            invites: Array.isArray(data?.invites) ? data.invites : [],
          };
        } catch (requestError) {
          return {
            organizationId,
            ...getRequestErrorDetails(
              requestError,
              "Failed to load organization invites.",
            ),
          };
        }
      }),
    );

    const successfulResults = results.filter((result) => !result.error);
    const failedResult = results.find((result) => result.error);

    if (failedResult) {
      setMfaRequiredForOrgAdmin(failedResult.code === "ORG_MFA_REQUIRED");
      setError(failedResult.error);
    }

    if (successfulResults.length) {
      setInvitesByOrganizationId((current) => {
        const next = { ...current };
        successfulResults.forEach(({ organizationId, invites }) => {
          next[organizationId] = invites;
        });
        return next;
      });
    }

    setInvitesLoadingByOrganizationId((current) => {
      const next = { ...current };
      organizationIds.forEach((organizationId) => {
        next[organizationId] = false;
      });
      return next;
    });
  };

  // This effect is intentionally mount-only; it bootstraps org state once.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let isMounted = true;

    const loadOrganizations = async () => {
      setLoading(true);
      clearRequestFeedback();

      try {
        const { data } = await apiClient.get(
          `${process.env.REACT_APP_BASE_URL}/organizations/mine`,
          { withCredentials: true },
        );

        const nextOrganizations = Array.isArray(data?.organizations)
          ? data.organizations
          : [];

        if (isMounted) {
          setOrganizations(nextOrganizations);
          const manageableIds = nextOrganizations
            .filter((organization) =>
              INVITE_CAPABLE_ROLES.includes(organization.membership?.orgRole),
            )
            .map((organization) => organization._id);
          const ownerIds = nextOrganizations
            .filter((organization) => organization.membership?.orgRole === "org_owner")
            .map((organization) => organization._id);
          await Promise.all([
            loadMembers(manageableIds),
            loadInvites(manageableIds),
            loadBillingSummaries(ownerIds),
            loadBillingLedger(ownerIds),
            loadBillingCycles(ownerIds),
            loadBillingBudgets(ownerIds),
            loadOrganizationPaymentMethods(ownerIds),
            loadOrganizationInvoices(ownerIds),
            loadOrganizationIdentityProviders(ownerIds),
          ]);
        }
      } catch (err) {
        if (isMounted) {
          applyRequestError(err, "Failed to load organizations.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadOrganizations();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const redirectAction = getOrganizationRedirectAction(location.search);
    if (loading || !redirectAction) {
      return;
    }

    let cancelled = false;

    const handleRedirectAction = async () => {
      if (!redirectAction.organizationId) {
        setSuccess("");
        setError(
          getOrganizationSsoRedirectMessage(
            redirectAction.providerType,
            redirectAction.errorCode,
          ),
        );
        navigate(
          {
            pathname: location.pathname,
            search: clearOrganizationRedirectParams(location.search),
          },
          { replace: true },
        );
        return;
      }

      const targetOrganization = organizations.find(
        (organization) => organization._id === redirectAction.organizationId,
      );

      if (!targetOrganization) {
        setError(
          redirectAction.type === "sso-microsoft-success" ||
            redirectAction.type === "sso-microsoft-error"
            ? "Organization sign-in redirect could not be resolved."
            : "Organization billing redirect could not be resolved.",
        );
        navigate(
          {
            pathname: location.pathname,
            search: clearOrganizationRedirectParams(location.search),
          },
          { replace: true },
        );
        return;
      }

      dispatch(selectWorkspace(`org:${targetOrganization._id}`));
      setExpandedOrganizations((current) => ({
        ...current,
        [targetOrganization._id]: true,
      }));
      setOrganizationSectionTabs((current) => ({
        ...current,
        [targetOrganization._id]:
          redirectAction.type === "sso-microsoft-success" ||
          redirectAction.type === "sso-microsoft-error"
            ? "identity"
            : "billing",
      }));
      setOrganizationBillingTabs((current) => ({
        ...current,
        [targetOrganization._id]: "billing",
      }));

      if (
        redirectAction.type === "sso-microsoft-success" ||
        redirectAction.type === "sso-microsoft-error"
      ) {
        await loadOrganizationIdentityProviders([targetOrganization._id]);
      } else {
        await Promise.all([
          loadBillingSummaries([targetOrganization._id]),
          loadBillingLedger([targetOrganization._id]),
          loadBillingCycles([targetOrganization._id]),
          loadOrganizationPaymentMethods([targetOrganization._id]),
          loadOrganizationInvoices([targetOrganization._id]),
        ]);
      }
      if (cancelled) return;

      if (redirectAction.type === "billing-setup-success") {
        setError("");
        setSuccess(`Updated billing setup for ${targetOrganization.name}.`);
      } else if (redirectAction.type === "billing-setup-canceled") {
        setError("");
        setSuccess(`Billing setup for ${targetOrganization.name} was canceled.`);
      } else if (redirectAction.type === "credit-purchase-success") {
        setError("");
        setSuccess(`Completed a credit purchase for ${targetOrganization.name}.`);
      } else if (redirectAction.type === "credit-purchase-canceled") {
        setError("");
        setSuccess(`Credit purchase for ${targetOrganization.name} was canceled.`);
      } else if (redirectAction.type === "sso-microsoft-success") {
        setError("");
        setSuccess(`Microsoft sign-in completed for ${targetOrganization.name}.`);
      } else if (redirectAction.type === "sso-microsoft-error") {
        setSuccess("");
        setError(
          getOrganizationSsoRedirectMessage(
            redirectAction.providerType,
            redirectAction.errorCode,
          ),
        );
      }

      navigate(
        {
          pathname: location.pathname,
          search: clearOrganizationRedirectParams(location.search),
        },
        { replace: true },
      );
    };

    handleRedirectAction();

    return () => {
      cancelled = true;
    };
  }, [dispatch, loading, location.pathname, location.search, navigate, organizations]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations`,
        { name },
        { withCredentials: true },
      );

      setOrganizations((current) => [
        {
          ...data.organization,
          membership: data.membership,
        },
        ...current,
      ]);
      dispatch(fetchWorkspaces());
      if (data?.organization?._id) {
        dispatch(selectWorkspace(`org:${data.organization._id}`));
        setExpandedOrganizations((current) => ({
          ...current,
          [data.organization._id]: true,
        }));
        await Promise.all([
          refreshOrganizationBilling(data.organization._id),
          loadOrganizationIdentityProviders([data.organization._id]),
        ]);
      }
      setSuccess(`Created ${data.organization.name}.`);
      setName("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create organization.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateOrganizationMembers = (organizationId, updater) => {
    setMembersByOrganizationId((current) => ({
      ...current,
      [organizationId]: updater(current[organizationId] || []),
    }));
  };

  const handleBillingDraftChange = (organizationId, field, value) => {
    setBillingProfileDrafts((current) => ({
      ...current,
      [organizationId]: {
        ...(current[organizationId] || billingDraftFromData(null)),
        [field]: value,
      },
    }));
  };

  const handleBillingAddressChange = (organizationId, field, value) => {
    setBillingProfileDrafts((current) => {
      const existing = current[organizationId] || billingDraftFromData(null);
      return {
        ...current,
        [organizationId]: {
          ...existing,
          billingAddress: { ...(existing.billingAddress || {}), [field]: value },
        },
      };
    });
  };

  const handleBillingProfileSave = async (organization) => {
    const draft =
      billingProfileDrafts[organization._id] || billingDraftFromData(null);

    setBillingSavingOrgId(organization._id);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.patch(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/profile`,
        {
          billingEmail: draft.billingEmail,
          billingName: draft.billingName,
          billingCurrency: draft.billingCurrency,
          billingAddress: draft.billingAddress,
          taxIdType: draft.taxIdType,
          taxIdValue: draft.taxIdValue,
        },
        { withCredentials: true },
      );

      setBillingByOrganizationId((current) => ({
        ...current,
        [organization._id]: data?.billing || current[organization._id],
      }));
      setBillingProfileDrafts((current) => ({
        ...current,
        [organization._id]: billingDraftFromData(data?.billing),
      }));
      setSuccess(`Updated billing profile for ${organization.name}.`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to update organization billing profile.",
      );
    } finally {
      setBillingSavingOrgId("");
    }
  };

  const handleMemberBudgetSave = async (organization, member) => {
    const actionKey = `${organization._id}:${member._id}:member-budget`;
    const draft = getMemberBudgetDraft(memberBudgetDrafts[member._id], member);

    setBillingActionKey(actionKey);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.patch(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/members/${member._id}/budget`,
        {
          limit: draft.limit === "" ? null : Number(draft.limit),
          resetIntervalCount:
            draft.resetIntervalCount === "" ? null : Number(draft.resetIntervalCount),
          resetIntervalUnit: draft.resetIntervalUnit || null,
          resetStartAt: draft.resetStartAt || null,
        },
        { withCredentials: true },
      );

      setBillingBudgetsByOrganizationId((current) => ({
        ...current,
        [organization._id]: {
          members: (current[organization._id]?.members || []).map((item) =>
            item._id === member._id ? data.member : item,
          ),
          projects: current[organization._id]?.projects || [],
        },
      }));
      setMemberBudgetDrafts((current) => ({
        ...current,
        [member._id]: buildMemberBudgetDraft(data?.member),
      }));
      setSuccess(`Updated budget for ${member.user?.email || "member"}.`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to update organization member budget.",
      );
    } finally {
      setBillingActionKey("");
    }
  };

  const handleMemberBudgetReset = async (organization, member) => {
    const actionKey = `${organization._id}:${member._id}:member-budget-reset`;

    setBillingActionKey(actionKey);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/members/${member._id}/budget/reset`,
        {},
        { withCredentials: true },
      );

      setBillingBudgetsByOrganizationId((current) => ({
        ...current,
        [organization._id]: {
          members: (current[organization._id]?.members || []).map((item) =>
            item._id === member._id ? data.member : item,
          ),
          projects: current[organization._id]?.projects || [],
        },
      }));
      setMemberBudgetDrafts((current) => ({
        ...current,
        [member._id]: buildMemberBudgetDraft(data?.member),
      }));
      setSuccess(`Reset usage for ${member.user?.email || "member"}.`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to reset organization member budget.",
      );
    } finally {
      setBillingActionKey("");
    }
  };

  const handleProjectBudgetSave = async (organization, project) => {
    const actionKey = `${organization._id}:${project._id}:project-budget`;
    const draft =
      projectBudgetDrafts[project._id] ?? toBudgetDraftValue(project?.budget?.limit);

    setBillingActionKey(actionKey);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.patch(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/projects/${project._id}/budget`,
        { limit: draft === "" ? null : Number(draft) },
        { withCredentials: true },
      );

      setBillingBudgetsByOrganizationId((current) => ({
        ...current,
        [organization._id]: {
          members: current[organization._id]?.members || [],
          projects: (current[organization._id]?.projects || []).map((item) =>
            item._id === project._id ? data.project : item,
          ),
        },
      }));
      setProjectBudgetDrafts((current) => ({
        ...current,
        [project._id]: toBudgetDraftValue(data?.project?.budget?.limit),
      }));
      setSuccess(`Updated project budget for ${project.name}.`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to update organization project budget.",
      );
    } finally {
      setBillingActionKey("");
    }
  };

  const handleStartOrganizationSubscription = async (organization, planKey) => {
    const actionKey = `${organization._id}:subscribe:${planKey}`;
    setBillingActionKey(actionKey);
    clearRequestFeedback();
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/subscription/start`,
        { key: planKey },
        { withCredentials: true },
      );

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      await refreshOrganizationBilling(organization._id);

      if (data?.upgraded) {
        setSuccess(`Upgraded ${organization.name} to the ${planKey} plan.`);
      } else if (data?.downgradeScheduled) {
        setSuccess(`Scheduled ${organization.name} to move to the ${planKey} plan at the next billing period.`);
      } else if (data?.reactivated) {
        setSuccess(`Reactivated ${organization.name}'s ${planKey} subscription.`);
      } else if (data?.alreadySubscribed) {
        setSuccess(data.message || `${organization.name} is already on the ${planKey} plan.`);
      } else {
        setSuccess(`Activated the ${planKey} plan for ${organization.name}.`);
      }
    } catch (requestError) {
      applyRequestError(requestError, "Failed to start organization subscription.");
    } finally {
      setBillingActionKey("");
    }
  };

  const handleStartOrganizationFreeTrial = async (organization) => {
    const promoCode = String(
      trialPromoCodesByOrganizationId[organization._id] || "",
    ).trim();
    const actionKey = `${organization._id}:start-free-trial`;

    setBillingActionKey(actionKey);
    clearRequestFeedback();
    setSuccess("");
    setTrialMessagesByOrganizationId((current) => ({
      ...current,
      [organization._id]: "",
    }));

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/trial/start`,
        { promoCode },
        { withCredentials: true },
      );

      await refreshOrganizationBilling(organization._id);
      setTrialInputsOpenByOrganizationId((current) => ({
        ...current,
        [organization._id]: false,
      }));
      setTrialPromoCodesByOrganizationId((current) => ({
        ...current,
        [organization._id]: "",
      }));
      setSuccess(data?.message || `Activated a free trial for ${organization.name}.`);
    } catch (requestError) {
      const message = applyRequestError(
        requestError,
        "Failed to activate organization free trial.",
      );
      setTrialMessagesByOrganizationId((current) => ({
        ...current,
        [organization._id]: message,
      }));
    } finally {
      setBillingActionKey("");
    }
  };

  const handleCancelOrganizationSubscription = async (organization) => {
    const confirmed = window.confirm(
      `Cancel ${organization.name}'s subscription at the end of the current billing period?`,
    );
    if (!confirmed) return;

    const actionKey = `${organization._id}:cancel-subscription`;
    setBillingActionKey(actionKey);
    clearRequestFeedback();
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/subscription/cancel`,
        {},
        { withCredentials: true },
      );

      await refreshOrganizationBilling(organization._id);
      setSuccess(data?.message || `Subscription for ${organization.name} will cancel at period end.`);
    } catch (requestError) {
      applyRequestError(requestError, "Failed to cancel organization subscription.");
    } finally {
      setBillingActionKey("");
    }
  };

  const handleStartOrganizationCreditPurchase = async (organization, packKey) => {
    const actionKey = `${organization._id}:buy-credits:${packKey}`;
    setBillingActionKey(actionKey);
    clearRequestFeedback();
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/credits/purchase`,
        { packKey },
        { withCredentials: true },
      );

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      await refreshOrganizationBilling(organization._id);
      setSuccess(
        data?.creditsAdded
          ? `Added ${data.creditsAdded} credits to ${organization.name}.`
          : `Started a credit purchase for ${organization.name}.`,
      );
    } catch (requestError) {
      applyRequestError(requestError, "Failed to start organization credit purchase.");
    } finally {
      setBillingActionKey("");
    }
  };

  const handleAddOrganizationPaymentMethod = async (organization) => {
    const actionKey = `${organization._id}:add-payment-method`;
    setBillingActionKey(actionKey);
    clearRequestFeedback();
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/payment-methods/setup-session`,
        {},
        { withCredentials: true },
      );

      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (requestError) {
      applyRequestError(requestError, "Failed to start organization payment method setup.");
    } finally {
      setBillingActionKey("");
    }
  };

  const handleSetOrganizationDefaultPaymentMethod = async (organization, paymentMethodId) => {
    const actionKey = `${organization._id}:default-payment:${paymentMethodId}`;
    setBillingActionKey(actionKey);
    clearRequestFeedback();
    setSuccess("");

    try {
      await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/payment-methods/default`,
        { paymentMethodId },
        { withCredentials: true },
      );

      await refreshOrganizationBilling(organization._id);
      setSuccess(`Updated default payment method for ${organization.name}.`);
    } catch (requestError) {
      applyRequestError(requestError, "Failed to update organization default payment method.");
    } finally {
      setBillingActionKey("");
    }
  };

  const handleDeleteOrganizationPaymentMethod = async (organization, paymentMethodId) => {
    const confirmed = window.confirm("Delete this organization payment method?");
    if (!confirmed) return;

    const actionKey = `${organization._id}:delete-payment:${paymentMethodId}`;
    setBillingActionKey(actionKey);
    clearRequestFeedback();
    setSuccess("");

    try {
      await apiClient.delete(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/billing/payment-methods`,
        {
          withCredentials: true,
          data: { paymentMethodId },
        },
      );

      await refreshOrganizationBilling(organization._id);
      setSuccess(`Deleted payment method from ${organization.name}.`);
    } catch (requestError) {
      applyRequestError(requestError, "Failed to delete organization payment method.");
    } finally {
      setBillingActionKey("");
    }
  };

  const handleMemberRoleChange = async (organization, member) => {
    const nextRole = memberRoleDrafts[member._id] || member.orgRole;
    const actionKey = `${organization._id}:${member._id}:role`;
    setMemberActionKey(actionKey);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.patch(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/members/${member._id}`,
        { orgRole: nextRole },
        { withCredentials: true },
      );

      updateOrganizationMembers(organization._id, (members) =>
        members.map((item) =>
          item._id === member._id ? { ...item, orgRole: data.membership.orgRole } : item,
        ),
      );
      setSuccess(`Updated ${member.user?.email || "member"} to ${data.membership.orgRole}.`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to update organization member role.",
      );
      setMemberRoleDrafts((current) => ({
        ...current,
        [member._id]: member.orgRole,
      }));
    } finally {
      setMemberActionKey("");
    }
  };

  const handleRemoveMember = async (organization, member) => {
    const memberLabel = member.user?.email || `${member.user?.firstName || ""} ${member.user?.lastName || ""}`.trim() || "this member";
    const confirmed = window.confirm(
      `Remove ${memberLabel} from ${organization.name}?`,
    );

    if (!confirmed) {
      return;
    }

    const actionKey = `${organization._id}:${member._id}:remove`;
    setMemberActionKey(actionKey);
    setError("");
    setSuccess("");

    try {
      await apiClient.delete(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/members/${member._id}`,
        { withCredentials: true },
      );

      updateOrganizationMembers(organization._id, (members) =>
        members.filter((item) => item._id !== member._id),
      );
      setSuccess(`Removed ${member.user?.email || "member"} from ${organization.name}.`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to remove organization member.",
      );
    } finally {
      setMemberActionKey("");
    }
  };

  const handleInviteSubmit = async (event, organization) => {
    event.preventDefault();

    const email = String(inviteEmails[organization._id] || "").trim();
    const invitedRole = inviteRoles[organization._id] || "member";

    setInviteSubmittingOrgId(organization._id);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/invites`,
        { email, invitedRole },
        { withCredentials: true },
      );

      setInviteEmails((current) => ({ ...current, [organization._id]: "" }));
      setInviteRoles((current) => ({ ...current, [organization._id]: "member" }));
      await loadInvites([organization._id]);
      setSuccess(
        data?.emailDelivery === "failed"
          ? `Invite created for ${email}, but email delivery failed.`
          : `Invite sent to ${email}.`,
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send organization invite.");
    } finally {
      setInviteSubmittingOrgId("");
    }
  };

  const handleRevokeInvite = async (organization, invite) => {
    const inviteLabel = invite.email || "this invite";
    const confirmed = window.confirm(
      `Revoke the invitation for ${inviteLabel} from ${organization.name}?`,
    );

    if (!confirmed) {
      return;
    }

    const actionKey = `${organization._id}:${invite._id}:revoke`;
    setInviteActionKey(actionKey);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.delete(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organization._id}/invites/${invite._id}`,
        { withCredentials: true },
      );

      setInvitesByOrganizationId((current) => ({
        ...current,
        [organization._id]: (current[organization._id] || []).map((item) =>
          item._id === invite._id
            ? {
                ...item,
                status: data?.invite?.status || "revoked",
                revokedAt: data?.invite?.revokedAt || item.revokedAt,
              }
            : item,
        ),
      }));
      setSuccess(`Revoked invite for ${invite.email || "organization invite"}.`);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to revoke organization invite.",
      );
    } finally {
      setInviteActionKey("");
    }
  };

  const handleOpenTransferDialog = (organization, member) => {
    setTransferDialog({
      organizationId: organization._id,
      organizationName: organization.name,
      memberId: member._id,
      memberEmail: member.user?.email || "",
      memberName:
        `${member.user?.firstName || ""} ${member.user?.lastName || ""}`.trim() ||
        member.user?.email ||
        "this member",
    });
    setTransferConfirmationText("");
    setError("");
    setSuccess("");
  };

  const handleCloseTransferDialog = () => {
    if (memberActionKey.endsWith(":transfer")) return;
    setTransferDialog(null);
    setTransferConfirmationText("");
  };

  const handleTransferOwnership = async () => {
    if (!transferDialog) return;

    const actionKey = `${transferDialog.organizationId}:${transferDialog.memberId}:transfer`;
    setMemberActionKey(actionKey);
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/organizations/${transferDialog.organizationId}/ownership-transfer`,
        {
          targetMembershipId: transferDialog.memberId,
          confirmationText: transferConfirmationText.trim(),
        },
        { withCredentials: true },
      );

      setOrganizations((current) =>
        current.map((organization) =>
          organization._id === transferDialog.organizationId
            ? {
                ...organization,
                membership: {
                  ...organization.membership,
                  orgRole: data.previousOwnerMembership.orgRole,
                },
              }
            : organization,
        ),
      );

      updateOrganizationMembers(transferDialog.organizationId, (members) =>
        members.map((member) => {
          if (member._id === data.previousOwnerMembership._id) {
            return { ...member, orgRole: data.previousOwnerMembership.orgRole };
          }

          if (member._id === data.newOwnerMembership._id) {
            return { ...member, orgRole: data.newOwnerMembership.orgRole };
          }

          return member;
        }),
      );

      setMemberRoleDrafts((current) => ({
        ...current,
        [data.previousOwnerMembership._id]: data.previousOwnerMembership.orgRole,
        [data.newOwnerMembership._id]: data.newOwnerMembership.orgRole,
      }));
      dispatch(fetchWorkspaces());
      setSuccess(
        `Transferred ownership of ${transferDialog.organizationName} to ${transferDialog.memberEmail || transferDialog.memberName}.`,
      );
      setTransferDialog(null);
      setTransferConfirmationText("");
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to transfer organization ownership.",
      );
    } finally {
      setMemberActionKey("");
    }
  };

  const handleOpenDeleteOrgDialog = (organization, otherActiveMembers) => {
    setDeleteOrgDialog({
      organizationId: organization._id,
      organizationName: organization.name,
      otherActiveMembers,
    });
    setDeleteOrgConfirmationText("");
    setDeleteOrgBlocker("");
    setError("");
    setSuccess("");
  };

  const handleCloseDeleteOrgDialog = () => {
    if (deletingOrganizationId) return;
    setDeleteOrgDialog(null);
    setDeleteOrgConfirmationText("");
    setDeleteOrgBlocker("");
  };

  const handleDeleteOrganization = async () => {
    if (!deleteOrgDialog) return;

    const { organizationId, organizationName } = deleteOrgDialog;
    setDeletingOrganizationId(organizationId);
    setDeleteOrgBlocker("");
    setError("");
    setSuccess("");

    try {
      const { data } = await apiClient.delete(
        `${process.env.REACT_APP_BASE_URL}/organizations/${organizationId}`,
        {
          withCredentials: true,
          data: { confirmationText: deleteOrgConfirmationText.trim() },
        },
      );

      setOrganizations((current) =>
        current.filter((organization) => organization._id !== organizationId),
      );

      // Leaving the app pointed at a deleted workspace would 403 every subsequent request.
      if (selectedWorkspaceKey === `org:${organizationId}`) {
        dispatch(selectWorkspace("personal"));
      }
      dispatch(fetchWorkspaces());

      const failed = data?.failures?.length || 0;
      setSuccess(
        failed > 0
          ? `Deleted ${organizationName}. ${data.projectsDeleted} project(s) removed, ` +
            `${failed} could not be cleaned up and need an operator.`
          : `Deleted ${organizationName} and its ${data?.projectsDeleted ?? 0} project(s).`,
      );
      setDeleteOrgDialog(null);
      setDeleteOrgConfirmationText("");
    } catch (requestError) {
      // members_remain / run_in_flight are instructions, not failures — keep them in the
      // dialog next to the button rather than in the page-level error bar.
      const code = requestError?.response?.data?.code;
      const message = requestError?.response?.data?.message;
      if (code === "members_remain" || code === "run_in_flight") {
        setDeleteOrgBlocker(message);
      } else {
        applyRequestError(requestError, "Failed to delete the organization.");
        setDeleteOrgDialog(null);
      }
    } finally {
      setDeletingOrganizationId("");
    }
  };

  return (
    <Box className="settings-section">
      <Typography variant="h6" gutterBottom>
        Organizations
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Create an organization to manage members and shared access in later phases.
      </Typography>

      {error ? (
        <Alert
          severity={mfaRequiredForOrgAdmin ? "warning" : "error"}
          sx={{ mb: 2 }}
          action={
            mfaRequiredForOrgAdmin ? (
              <Button color="inherit" size="small" onClick={handleMfaRequiredNavigation}>
                Enable MFA
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "grid",
          gap: 2,
          maxWidth: 520,
          mb: 4,
        }}
      >
        <TextField
          label="Organization name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Example: Acme Labs"
          required
          inputProps={{ minLength: 2, maxLength: 200 }}
          disabled={submitting}
        />
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || name.trim().length < 2}
            sx={actionButtonSx}
          >
            {submitting ? "Creating..." : "Create organization"}
          </Button>
        </Box>
      </Box>

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography gutterBottom sx={organizationsPageHeadingSx}>
          Your organizations
        </Typography>
        {renderHelpButton("org-overview", "Organizations")}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading organizations...</Typography>
        </Box>
      ) : organizations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No organizations yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {organizations.map((organization) => (
            (() => {
              const isExpanded = Boolean(expandedOrganizations[organization._id]);
              const pendingInviteCount = getPendingInviteCount(organization._id);
              const memberCount = (membersByOrganizationId[organization._id] || []).length;
              const inviteCount = (invitesByOrganizationId[organization._id] || []).length;
              const activeTab = getOrganizationTabValue(organization);
              const activeBillingTab = getOrganizationBillingTabValue(organization._id);
              const organizationBilling = billingByOrganizationId[organization._id] || null;
              const organizationBillingBudgets =
                billingBudgetsByOrganizationId[organization._id] || { members: [], projects: [] };
              const editableMemberBudgets = organizationBillingBudgets.members.filter(
                (member) => member.orgRole !== "viewer",
              );
              const cappedMemberBudgets = organizationBillingBudgets.members
                .filter((member) => getBudgetLimitValue(member?.budget) !== null)
                .sort((left, right) => {
                  const remainingDelta =
                    (getBudgetRemainingValue(left?.budget) ?? Number.POSITIVE_INFINITY) -
                    (getBudgetRemainingValue(right?.budget) ?? Number.POSITIVE_INFINITY);
                  if (remainingDelta !== 0) return remainingDelta;

                  return (getBudgetUsagePercent(right?.budget) ?? 0) -
                    (getBudgetUsagePercent(left?.budget) ?? 0);
                });
              const cappedProjectBudgets = organizationBillingBudgets.projects
                .filter((project) => getBudgetLimitValue(project?.budget) !== null)
                .sort((left, right) => {
                  const remainingDelta =
                    (getBudgetRemainingValue(left?.budget) ?? Number.POSITIVE_INFINITY) -
                    (getBudgetRemainingValue(right?.budget) ?? Number.POSITIVE_INFINITY);
                  if (remainingDelta !== 0) return remainingDelta;

                  return (getBudgetUsagePercent(right?.budget) ?? 0) -
                    (getBudgetUsagePercent(left?.budget) ?? 0);
                });
              const memberDashboardFilter =
                memberDashboardFilters[organization._id] || "all";
              const projectDashboardFilter =
                projectDashboardFilters[organization._id] || "all";
              const filteredMemberDashboardItems = filterBudgetItems(
                cappedMemberBudgets,
                memberDashboardFilter,
              );
              const filteredProjectDashboardItems = filterBudgetItems(
                cappedProjectBudgets,
                projectDashboardFilter,
              );
              const usageDebitEntries = (billingLedgerByOrganizationId[organization._id] || [])
                .filter((item) => item.entryType === "usage_debit")
                .slice(0, 20);
              const recentUsageEntries = usageDebitEntries.slice(0, 5);
              const usageActivityGroups = buildUsageActivityGroups(usageDebitEntries)
                .slice(0, 5);
              const organizationPaymentMethods =
                paymentMethodsByOrganizationId[organization._id] || [];
              const identityProviders =
                identityProvidersByOrganizationId[organization._id] || [];
              const identityProvidersLoading =
                identityProvidersLoadingByOrganizationId[organization._id];
              const selectedIdentityProviderType = getSelectedIdentityProviderType(
                organization._id,
                identityProviders,
              );
              const selectedIdentityProvider =
                identityProviders.find(
                  (provider) => provider.providerType === selectedIdentityProviderType,
                ) || null;
              const identityProviderDraft = getIdentityProviderDraft(
                organization._id,
                selectedIdentityProviderType,
                identityProviders,
              );
              const identityProviderValidationState = getIdentityProviderValidationState(
                organization._id,
                selectedIdentityProviderType,
              );
              const identityProviderClientErrors =
                getIdentityProviderClientValidationErrors(identityProviderDraft, {
                  hasExistingSecret: Boolean(selectedIdentityProvider?.hasClientSecret),
                });
              const shouldShowIdentityProviderFieldError = (fieldName) =>
                Boolean(
                  identityProviderValidationState.submitAttempted ||
                    identityProviderValidationState.touchedFields?.[fieldName],
                );
              const getIdentityProviderFieldError = (fieldName) =>
                shouldShowIdentityProviderFieldError(fieldName)
                  ? identityProviderClientErrors[fieldName] || ""
                  : "";
              const hasUnsavedIdentityProviderDraft = hasUnsavedIdentityProviderChanges(
                identityProviderDraft,
                selectedIdentityProvider,
              );
              const canValidateSelectedIdentityProvider =
                Boolean(selectedIdentityProvider) &&
                !hasUnsavedIdentityProviderDraft &&
                Object.keys(identityProviderClientErrors).length === 0;
              const selectedIdentityProviderLaunchMetadata =
                getOrganizationSsoProviderMetadata(selectedIdentityProviderType);
              const canLaunchOrganizationSso =
                Boolean(selectedIdentityProviderLaunchMetadata.providerKey) &&
                Boolean(selectedIdentityProvider) &&
                selectedIdentityProvider.status === "active" &&
                canValidateSelectedIdentityProvider;

              return (
            <Box
              key={organization._id}
              sx={{
                border: "1px solid rgba(255, 255, 255, 0.14)",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                background:
                  "linear-gradient(180deg, rgba(20, 28, 68, 0.96) 0%, rgba(26, 35, 82, 0.98) 100%)",
                boxShadow: "0 18px 40px rgba(7, 12, 38, 0.28)",
                color: "#f5f7ff",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={organizationCardTitleSx}>
                    {organization.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.8)" }}>
                    /{organization.slug}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={organization.membership?.orgRole || "member"}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  <IconButton
                    aria-label={isExpanded ? `Collapse ${organization.name}` : `Expand ${organization.name}`}
                    onClick={() => toggleOrganizationExpanded(organization._id)}
                    size="small"
                    sx={organizationCollapseToggleSx}
                  >
                    {isExpanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
                  </IconButton>
                </Stack>
              </Stack>

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {INVITE_CAPABLE_ROLES.includes(organization.membership?.orgRole) ? (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  useFlexGap
                  sx={{ mt: 1.5, flexWrap: "wrap" }}
                >
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      borderRadius: 2,
                      backgroundColor: "rgba(24, 35, 83, 0.92)",
                      border: "1px solid rgba(191, 205, 255, 0.16)",
                      minWidth: { xs: "100%", sm: 150 },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                      Role
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                      {ORG_ROLE_LABELS[organization.membership?.orgRole] || organization.membership?.orgRole}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      borderRadius: 2,
                      backgroundColor: "rgba(24, 35, 83, 0.92)",
                      border: "1px solid rgba(191, 205, 255, 0.16)",
                      minWidth: { xs: "100%", sm: 150 },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                      Members
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                      {memberCount}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.9,
                      borderRadius: 2,
                      backgroundColor:
                        pendingInviteCount > 0
                          ? "rgba(255, 183, 77, 0.12)"
                          : "rgba(24, 35, 83, 0.92)",
                      border:
                        pendingInviteCount > 0
                          ? "1px solid rgba(255, 183, 77, 0.32)"
                          : "1px solid rgba(191, 205, 255, 0.16)",
                      minWidth: { xs: "100%", sm: 150 },
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                      Pending invites
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: pendingInviteCount > 0 ? "#ffd08a" : "#f8fbff",
                        fontWeight: 700,
                      }}
                    >
                      {pendingInviteCount}
                    </Typography>
                  </Box>
                  {organization.membership?.orgRole === "org_owner" ? (
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.9,
                        borderRadius: 2,
                        backgroundColor: "rgba(24, 35, 83, 0.92)",
                        border: "1px solid rgba(191, 205, 255, 0.16)",
                        minWidth: { xs: "100%", sm: 170 },
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                        Billing status
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                        {BILLING_STATUS_LABELS[billingByOrganizationId[organization._id]?.billingStatus] || "Unconfigured"}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              ) : null}

              {INVITE_CAPABLE_ROLES.includes(organization.membership?.orgRole) ? (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      backgroundColor: "rgba(112, 118, 136, 0.24)",
                      overflow: "hidden",
                    }}
                  >
                    <Tabs
                      value={activeTab}
                      onChange={(_event, nextTab) =>
                        handleOrganizationTabChange(organization._id, nextTab)
                      }
                      textColor="inherit"
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        minHeight: 52,
                        px: 1,
                        borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                        background:
                          "linear-gradient(180deg, rgba(82, 88, 106, 0.38) 0%, rgba(108, 115, 134, 0.24) 100%)",
                        "& .MuiTabs-indicator": {
                          backgroundColor: "#f2b84b",
                          height: 4,
                          borderRadius: 999,
                        },
                        "& .MuiTab-root": {
                          minHeight: 52,
                          color: "rgba(255, 246, 224, 0.9)",
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 1.5,
                          mx: 0.5,
                          my: 0.75,
                          minWidth: 0,
                          backgroundColor: "rgba(88, 74, 50, 0.34)",
                          border: "1px solid rgba(219, 188, 128, 0.16)",
                          transition: "background-color 160ms ease, color 160ms ease, box-shadow 160ms ease",
                        },
                        "& .MuiTab-textColorInherit": {
                          color: "rgba(255, 246, 224, 0.9)",
                        },
                        "& .MuiTab-root:hover": {
                          backgroundColor: "rgba(117, 95, 58, 0.48)",
                          color: "#f8fbff",
                        },
                        "& .MuiTab-root.Mui-selected": {
                          color: "#f8fbff",
                          backgroundColor: "rgba(166, 120, 36, 0.72)",
                          borderColor: "rgba(245, 201, 115, 0.42)",
                          boxShadow: "inset 0 0 0 1px rgba(255, 225, 160, 0.16)",
                        },
                      }}
                    >
                      {organization.membership?.orgRole === "org_owner" ? (
                        <Tab
                          value="billing"
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <span>Billing</span>
                            </Box>
                          }
                        />
                      ) : null}
                      {organization.membership?.orgRole === "org_owner" && ENTERPRISE_SSO_ENABLED ? (
                        <Tab
                          value="identity"
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <span>Identity</span>
                              <Typography component="span" variant="caption" sx={{ color: "inherit", opacity: 0.82 }}>
                                {identityProviders.length}
                              </Typography>
                            </Box>
                          }
                        />
                      ) : null}
                      <Tab
                        value="invites"
                        label={
                          <Badge
                            badgeContent={pendingInviteCount > 0 ? pendingInviteCount : null}
                            color="warning"
                            overlap="rectangular"
                            sx={{
                              "& .MuiBadge-badge": {
                                fontWeight: 800,
                                minWidth: 18,
                                height: 18,
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pr: pendingInviteCount > 0 ? 1.25 : 0 }}>
                              <span>Invites</span>
                              <Typography component="span" variant="caption" sx={{ color: "inherit", opacity: 0.82 }}>
                                {inviteCount}
                              </Typography>
                            </Box>
                          </Badge>
                        }
                      />
                      <Tab
                        value="members"
                        label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <span>Members</span>
                            <Typography component="span" variant="caption" sx={{ color: "inherit", opacity: 0.82 }}>
                              {memberCount}
                            </Typography>
                          </Box>
                        }
                      />
                    </Tabs>

                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                      {activeTab === "billing" &&
                      organization.membership?.orgRole === "org_owner" ? (
                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.5,
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.25}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                      >
                        <Box>
                          <Typography sx={billingTabHeadingSx}>
                            Plans & payments
                          </Typography>
                          <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                            Manage organization subscriptions, credit packs, payment methods, and billing contact details.
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => refreshOrganizationBilling(organization._id)}
                          disabled={
                            billingLoadingByOrganizationId[organization._id] ||
                            billingLedgerLoadingByOrganizationId[organization._id] ||
                            billingCyclesLoadingByOrganizationId[organization._id] ||
                            billingBudgetsLoadingByOrganizationId[organization._id] ||
                            paymentMethodsLoadingByOrganizationId[organization._id]
                          }
                          sx={outlinedActionButtonSx}
                        >
                          {billingLoadingByOrganizationId[organization._id] ||
                          billingLedgerLoadingByOrganizationId[organization._id] ||
                          billingCyclesLoadingByOrganizationId[organization._id] ||
                          billingBudgetsLoadingByOrganizationId[organization._id] ||
                          paymentMethodsLoadingByOrganizationId[organization._id]
                            ? "Refreshing..."
                            : "Refresh billing"}
                        </Button>
                      </Stack>

                      <Tabs
                        value={activeBillingTab}
                        onChange={(_event, nextTab) =>
                          handleOrganizationBillingTabChange(organization._id, nextTab)
                        }
                        textColor="inherit"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                          minHeight: 44,
                          px: 0.5,
                          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                          "& .MuiTabs-indicator": {
                            backgroundColor: "#f2b84b",
                            height: 3,
                            borderRadius: 999,
                          },
                          "& .MuiTab-root": {
                            minHeight: 44,
                            color: "rgba(255, 244, 219, 0.9)",
                            textTransform: "none",
                            fontWeight: 700,
                            minWidth: 0,
                            px: 2,
                            borderRadius: 1.25,
                            backgroundColor: "rgba(83, 69, 46, 0.34)",
                            border: "1px solid rgba(217, 186, 127, 0.14)",
                            transition: "background-color 160ms ease, color 160ms ease, border-color 160ms ease",
                          },
                          "& .MuiTab-textColorInherit": {
                            color: "rgba(255, 244, 219, 0.9)",
                          },
                          "& .MuiTab-root:hover": {
                            backgroundColor: "rgba(112, 92, 57, 0.46)",
                            color: "#f8fbff",
                          },
                          "& .MuiTab-root.Mui-selected": {
                            color: "#fff4d6",
                            backgroundColor: "rgba(163, 117, 34, 0.7)",
                            borderColor: "rgba(242, 198, 111, 0.38)",
                            boxShadow: "inset 0 0 0 1px rgba(255, 223, 153, 0.14)",
                          },
                        }}
                      >
                        <Tab value="billing" label="Plans & Payments" />
                        <Tab value="usage" label="Usage" />
                      </Tabs>

                      {billingLoadingByOrganizationId[organization._id] ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2">Loading billing summary...</Typography>
                        </Box>
                      ) : billingByOrganizationId[organization._id] ? (
                        <Stack spacing={activeBillingTab === "usage" ? 3.25 : 2.5}>
                          {activeBillingTab === "billing" ? (
                            <>
                              {renderOrganizationCollapsibleSection({
                                organizationId: organization._id,
                                organizationName: organization.name,
                                sectionKey: "billing-payment-methods",
                                title: "Saved payment methods",
                                description: "Manage the cards used for subscriptions and credit purchases.",
                                actionBelow: (
                                  <Button
                                    variant="contained"
                                    onClick={() => handleAddOrganizationPaymentMethod(organization)}
                                    disabled={billingActionKey === `${organization._id}:add-payment-method`}
                                    sx={actionButtonSx}
                                  >
                                    {billingActionKey === `${organization._id}:add-payment-method`
                                      ? "Opening..."
                                      : "Add payment method"}
                                  </Button>
                                ),
                                children: paymentMethodsLoadingByOrganizationId[organization._id] ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">Loading payment methods...</Typography>
                                  </Box>
                                ) : organizationPaymentMethods.length === 0 ? (
                                  <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                    No saved organization payment methods yet.
                                  </Typography>
                                ) : (
                                  <Stack spacing={1}>
                                    {organizationPaymentMethods.map((paymentMethod) => (
                                      <Box
                                        key={paymentMethod.id}
                                        sx={{
                                          border: "1px solid rgba(255, 255, 255, 0.1)",
                                          borderRadius: 2,
                                          px: 1.5,
                                          py: 1.25,
                                          backgroundColor: "rgba(112, 118, 136, 0.24)",
                                        }}
                                      >
                                        <Stack
                                          direction={{ xs: "column", md: "row" }}
                                          spacing={1}
                                          justifyContent="space-between"
                                          alignItems={{ xs: "flex-start", md: "center" }}
                                        >
                                          <Box>
                                            <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                              {(paymentMethod.brand || "card").toUpperCase()} ending in {paymentMethod.last4}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                              Expires {paymentMethod.exp_month}/{paymentMethod.exp_year}
                                            </Typography>
                                          </Box>
                                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                            {paymentMethod.isDefault ? (
                                              <Chip label="Default" size="small" color="success" variant="outlined" />
                                            ) : (
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                  handleSetOrganizationDefaultPaymentMethod(
                                                    organization,
                                                    paymentMethod.id,
                                                  )
                                                }
                                                disabled={
                                                  billingActionKey ===
                                                  `${organization._id}:default-payment:${paymentMethod.id}`
                                                }
                                                sx={secondaryRowActionButtonSx}
                                              >
                                                {billingActionKey ===
                                                `${organization._id}:default-payment:${paymentMethod.id}`
                                                  ? "Saving..."
                                                  : "Set default"}
                                              </Button>
                                            )}
                                            {!paymentMethod.isDefault ? (
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() =>
                                                  handleDeleteOrganizationPaymentMethod(
                                                    organization,
                                                    paymentMethod.id,
                                                  )
                                                }
                                                disabled={
                                                  billingActionKey ===
                                                  `${organization._id}:delete-payment:${paymentMethod.id}`
                                                }
                                                sx={secondaryRowDangerButtonSx}
                                              >
                                                {billingActionKey ===
                                                `${organization._id}:delete-payment:${paymentMethod.id}`
                                                  ? "Deleting..."
                                                  : "Delete"}
                                              </Button>
                                            ) : null}
                                          </Stack>
                                        </Stack>
                                      </Box>
                                    ))}
                                  </Stack>
                                ),
                              })}

                              {renderOrganizationCollapsibleSection({
                                organizationId: organization._id,
                                organizationName: organization.name,
                                sectionKey: "billing-subscription-plans",
                                title: "Subscription plans",
                                description: "Choose the organization plan and manage renewals.",
                                actionBelow: organizationBilling?.plan ? (
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleCancelOrganizationSubscription(organization)}
                                    disabled={billingActionKey === `${organization._id}:cancel-subscription`}
                                    sx={outlinedActionButtonSx}
                                  >
                                    {billingActionKey === `${organization._id}:cancel-subscription`
                                      ? "Canceling..."
                                      : organizationBilling?.cancelAtPeriodEnd
                                        ? "Cancellation scheduled"
                                        : "Cancel subscription"}
                                  </Button>
                                ) : null,
                                children: (
                                  <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25}>
                                    {ORG_SUBSCRIPTION_OPTIONS.map((planOption) => {
                                      const isCurrentPlan = organizationBilling?.plan === planOption.key;
                                      const isPendingPlan = organizationBilling?.pendingPlan === planOption.key;
                                      const isCurrentRankHigher =
                                        ORG_PLAN_RANK[organizationBilling?.plan] > ORG_PLAN_RANK[planOption.key];
                                      const isPendingDowngrade =
                                        isPendingPlan && isCurrentRankHigher;
                                      const subscribeKey = `${organization._id}:subscribe:${planOption.key}`;

                                      return (
                                        <Box
                                          key={planOption.key}
                                          sx={{
                                            flex: 1,
                                            display: "flex",
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.4,
                                            backgroundColor: isCurrentPlan
                                              ? "rgba(0, 230, 118, 0.1)"
                                              : "rgba(14, 24, 64, 0.82)",
                                            border: isCurrentPlan
                                              ? "1px solid rgba(0, 230, 118, 0.28)"
                                              : "1px solid rgba(255, 255, 255, 0.08)",
                                          }}
                                        >
                                          <Stack spacing={1} sx={{ flex: 1, height: "100%" }}>
                                            <Box sx={{ minHeight: 72 }}>
                                              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                                {planOption.name}
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                {planOption.price}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                                {planOption.creditsLabel}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ minHeight: 32, display: "flex", alignItems: "flex-start" }}>
                                              {isCurrentPlan ? (
                                                <Chip
                                                  label={
                                                    organizationBilling?.cancelAtPeriodEnd
                                                      ? "Active until period end"
                                                      : "Current plan"
                                                  }
                                                  size="small"
                                                  color="success"
                                                  variant="outlined"
                                                />
                                              ) : isPendingPlan ? (
                                                <Chip label="Scheduled next" size="small" color="warning" variant="outlined" />
                                              ) : null}
                                            </Box>
                                            {/* Flexible spacer instead of mt:"auto" on the button: MUI Stack's spacing rule
                                                (.MuiStack-root > :not(style) ~ :not(style)) outranks a child's own sx
                                                margin-top, so mt:"auto" was silently overridden and the cards only
                                                looked aligned while their content happened to be the same height. A
                                                grow-spacer is immune to that and bottom-aligns every action row. */}
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Button
                                              variant={isCurrentPlan || isPendingPlan ? "outlined" : "contained"}
                                              onClick={() => handleStartOrganizationSubscription(organization, planOption.key)}
                                              disabled={
                                                billingActionKey === subscribeKey ||
                                                isPendingPlan ||
                                                (isCurrentPlan && !organizationBilling?.cancelAtPeriodEnd)
                                              }
                                              sx={{
                                                ...(isCurrentPlan ? outlinedActionButtonSx : actionButtonSx),
                                                mt: "auto",
                                              }}
                                            >
                                              {billingActionKey === subscribeKey
                                                ? "Processing..."
                                                : isPendingPlan
                                                  ? isPendingDowngrade
                                                    ? "Scheduled downgrade"
                                                    : "Scheduled next"
                                                : isCurrentPlan
                                                  ? organizationBilling?.cancelAtPeriodEnd
                                                    ? "Reactivate"
                                                    : "Current"
                                                  : isCurrentRankHigher
                                                    ? "Schedule downgrade"
                                                    : "Choose plan"}
                                            </Button>
                                          </Stack>
                                        </Box>
                                      );
                                    })}
                                    {organizationBilling?.plan === "free_trial" ? (
                                      <Box
                                        sx={{
                                          flex: 1,
                                          display: "flex",
                                          borderRadius: 2,
                                          px: 1.5,
                                          py: 1.4,
                                          backgroundColor: "rgba(0, 230, 118, 0.1)",
                                          border: "1px solid rgba(0, 230, 118, 0.28)",
                                        }}
                                      >
                                        <Stack spacing={1} sx={{ flex: 1, height: "100%" }}>
                                          <Box sx={{ minHeight: 72 }}>
                                            <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                              Free trial active
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                              Starter plan access for organization billing.
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                              Ends {formatDateTime(organizationBilling?.subscriptionExpiresAt)}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ minHeight: 32, display: "flex", alignItems: "flex-start" }}>
                                            <Chip label="Trialing" size="small" color="success" variant="outlined" />
                                          </Box>
                                          <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)", mt: "auto" }}>
                                            {organizationBilling?.planCredits ?? 0} starter plan credits available.
                                          </Typography>
                                        </Stack>
                                      </Box>
                                    ) : null}
                                    {!organizationBilling?.plan && !organizationBilling?.pendingPlan ? (
                                      <Box
                                        sx={{
                                          flex: 1,
                                          display: "flex",
                                          borderRadius: 2,
                                          px: 1.5,
                                          py: 1.4,
                                          backgroundColor: "rgba(14, 24, 64, 0.82)",
                                          border: "1px solid rgba(255, 255, 255, 0.08)",
                                        }}
                                      >
                                        <Stack spacing={1} sx={{ flex: 1, height: "100%" }}>
                                          <Box sx={{ minHeight: 72 }}>
                                            <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                              Start free trial
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                              Use a promo code to unlock starter-plan trial access for this organization.
                                            </Typography>
                                          </Box>
                                          <Box sx={{ minHeight: 32, display: "flex", alignItems: "flex-start" }} />
                                          <Box sx={{ flexGrow: 1 }} />
                                          {trialInputsOpenByOrganizationId[organization._id] ? (
                                            <>
                                              <TextField
                                                fullWidth
                                                size="small"
                                                label="Promo code"
                                                value={trialPromoCodesByOrganizationId[organization._id] || ""}
                                                onChange={(event) =>
                                                  setTrialPromoCodesByOrganizationId((current) => ({
                                                    ...current,
                                                    [organization._id]: event.target.value,
                                                  }))
                                                }
                                                sx={darkInputSx}
                                              />
                                              {trialMessagesByOrganizationId[organization._id] ? (
                                                <Typography variant="caption" sx={{ color: "#ffb4b4" }}>
                                                  {trialMessagesByOrganizationId[organization._id]}
                                                </Typography>
                                              ) : null}
                                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: "auto" }}>
                                                <Button
                                                  variant="contained"
                                                  onClick={() => handleStartOrganizationFreeTrial(organization)}
                                                  disabled={
                                                    billingActionKey === `${organization._id}:start-free-trial` ||
                                                    !String(trialPromoCodesByOrganizationId[organization._id] || "").trim()
                                                  }
                                                  sx={actionButtonSx}
                                                >
                                                  {billingActionKey === `${organization._id}:start-free-trial`
                                                    ? "Activating..."
                                                    : "Activate free trial"}
                                                </Button>
                                                <Button
                                                  variant="outlined"
                                                  onClick={() => {
                                                    setTrialInputsOpenByOrganizationId((current) => ({
                                                      ...current,
                                                      [organization._id]: false,
                                                    }));
                                                    setTrialPromoCodesByOrganizationId((current) => ({
                                                      ...current,
                                                      [organization._id]: "",
                                                    }));
                                                    setTrialMessagesByOrganizationId((current) => ({
                                                      ...current,
                                                      [organization._id]: "",
                                                    }));
                                                  }}
                                                  sx={outlinedActionButtonSx}
                                                >
                                                  Cancel
                                                </Button>
                                              </Stack>
                                            </>
                                          ) : (
                                            <Button
                                              variant="contained"
                                              onClick={() =>
                                                setTrialInputsOpenByOrganizationId((current) => ({
                                                  ...current,
                                                  [organization._id]: true,
                                                }))
                                              }
                                              sx={{ ...actionButtonSx, mt: "auto" }}
                                            >
                                              Use promo code
                                            </Button>
                                          )}
                                        </Stack>
                                      </Box>
                                    ) : null}
                                  </Stack>
                                ),
                              })}
                            </>
                          ) : null}

                          {activeBillingTab === "usage" ? (
                          <Box
                            sx={{
                              border: "1px solid rgba(255, 255, 255, 0.12)",
                              borderRadius: 2,
                              backgroundColor: "rgba(255, 255, 255, 0.03)",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.5,
                                py: 1.25,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={billingSectionHeadingSx}>
                                  Usage overview
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  Track capped budgets, near-cap items, and recent usage signals without opening each editor row.
                                </Typography>
                              </Box>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                {renderHelpButton("usage-dashboard", "Usage overview")}
                                <IconButton
                                  aria-label={
                                    isUsageBudgetSectionExpanded(organization._id, "usage-dashboard")
                                      ? `Collapse usage overview for ${organization.name}`
                                      : `Expand usage overview for ${organization.name}`
                                  }
                                  onClick={() =>
                                    toggleUsageBudgetSectionExpanded(organization._id, "usage-dashboard")
                                  }
                                  size="small"
                                  sx={sectionCollapseToggleSx}
                                >
                                  {isUsageBudgetSectionExpanded(organization._id, "usage-dashboard") ? (
                                    <ExpandLessOutlined />
                                  ) : (
                                    <ExpandMoreOutlined />
                                  )}
                                </IconButton>
                              </Stack>
                            </Box>
                            <Collapse
                              in={isUsageBudgetSectionExpanded(organization._id, "usage-dashboard")}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ px: 1.5, pb: 1.5, display: "grid", gap: 1.25 }}>
                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  spacing={1.25}
                                  useFlexGap
                                  sx={{ flexWrap: "wrap" }}
                                >
                              <Box
                                sx={{
                                  minWidth: { xs: "100%", md: 180 },
                                  flex: 1,
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 1.25,
                                  backgroundColor: "rgba(14, 24, 64, 0.82)",
                                  border: "1px solid rgba(255, 255, 255, 0.08)",
                                }}
                              >
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                  Capped members
                                </Typography>
                                <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                  {cappedMemberBudgets.length}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  {cappedMemberBudgets.length
                                    ? `${sumBudgetRemaining(cappedMemberBudgets)} simulations remain across capped members`
                                    : "No member caps configured"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  minWidth: { xs: "100%", md: 180 },
                                  flex: 1,
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 1.25,
                                  backgroundColor: "rgba(14, 24, 64, 0.82)",
                                  border: "1px solid rgba(255, 255, 255, 0.08)",
                                }}
                              >
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                  Capped projects
                                </Typography>
                                <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                  {cappedProjectBudgets.length}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  {cappedProjectBudgets.length
                                    ? `${sumBudgetRemaining(cappedProjectBudgets)} simulations remain across capped projects`
                                    : "No project caps configured"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  minWidth: { xs: "100%", md: 180 },
                                  flex: 1,
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 1.25,
                                  backgroundColor: "rgba(14, 24, 64, 0.82)",
                                  border: "1px solid rgba(255, 255, 255, 0.08)",
                                }}
                              >
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                  Members near cap
                                </Typography>
                                <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                  {
                                    cappedMemberBudgets.filter(
                                      (member) => getBudgetStatus(member?.budget).label !== "Healthy",
                                    ).length
                                  }
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  Includes members already at cap or within the final 20%
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  minWidth: { xs: "100%", md: 180 },
                                  flex: 1,
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 1.25,
                                  backgroundColor: "rgba(14, 24, 64, 0.82)",
                                  border: "1px solid rgba(255, 255, 255, 0.08)",
                                }}
                              >
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                  Recent usage debits
                                </Typography>
                                <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                  {recentUsageEntries.length}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  {recentUsageEntries.length
                                    ? `Latest debit ${formatDateTime(recentUsageEntries[0]?.createdAt)}`
                                    : "No usage debits recorded yet"}
                                </Typography>
                              </Box>
                                </Stack>

                                <Box
                                  sx={{
                                    border: "1px solid rgba(255, 255, 255, 0.12)",
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                                    overflow: "hidden",
                                  }}
                                >
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1.25,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" sx={billingSectionHeadingSx}>
                                    Members closest to cap
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                    Review the most constrained member budgets without opening each editor row.
                                  </Typography>
                                </Box>
                                <IconButton
                                  aria-label={
                                    isUsageBudgetSectionExpanded(organization._id, "dashboard-members")
                                      ? `Collapse member dashboard for ${organization.name}`
                                      : `Expand member dashboard for ${organization.name}`
                                  }
                                  onClick={() =>
                                    toggleUsageBudgetSectionExpanded(
                                      organization._id,
                                      "dashboard-members",
                                    )
                                  }
                                  size="small"
                                  sx={nestedCollapseToggleSx}
                                >
                                  {isUsageBudgetSectionExpanded(organization._id, "dashboard-members") ? (
                                    <ExpandLessOutlined />
                                  ) : (
                                    <ExpandMoreOutlined />
                                  )}
                                </IconButton>
                              </Box>
                              <Collapse
                                in={isUsageBudgetSectionExpanded(organization._id, "dashboard-members")}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ px: 1.5, pb: 1.5 }}>
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", mb: 1.25 }}>
                                    {[
                                      { key: "all", label: "All capped" },
                                      { key: "near-cap", label: "Near cap" },
                                      { key: "at-cap", label: "At cap" },
                                    ].map((option) => (
                                      <Button
                                        key={`member-filter-${option.key}`}
                                        variant={memberDashboardFilter === option.key ? "contained" : "outlined"}
                                        onClick={() =>
                                          setMemberDashboardFilters((current) => ({
                                            ...current,
                                            [organization._id]: option.key,
                                          }))
                                        }
                                        sx={memberDashboardFilter === option.key ? actionButtonSx : outlinedActionButtonSx}
                                      >
                                        {option.label}
                                      </Button>
                                    ))}
                                  </Stack>
                                  {filteredMemberDashboardItems.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      {cappedMemberBudgets.length === 0
                                        ? "No capped members yet."
                                        : "No members match the current dashboard filter."}
                                    </Typography>
                                  ) : (
                                    <Box sx={{ maxHeight: { xl: 428 }, overflowY: "auto", pr: 0.5 }}>
                                      <Stack spacing={1}>
                                        {filteredMemberDashboardItems.slice(0, 5).map((member) => {
                                          const budgetStatus = getBudgetStatus(member?.budget);
                                          const budgetLimit = getBudgetLimitValue(member?.budget);
                                          const budgetRemaining = getBudgetRemainingValue(member?.budget);

                                          return (
                                            <Box
                                              key={`dashboard-member-${member._id}`}
                                              sx={{
                                                border: "1px solid rgba(255, 255, 255, 0.1)",
                                                borderRadius: 2,
                                                px: 1.5,
                                                py: 1.25,
                                                backgroundColor: "rgba(112, 118, 136, 0.24)",
                                              }}
                                            >
                                              <Stack
                                                direction={{ xs: "column", md: "row" }}
                                                spacing={1}
                                                justifyContent="space-between"
                                                alignItems={{ xs: "flex-start", md: "center" }}
                                              >
                                                <Box>
                                                  <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                                    {member.user?.firstName} {member.user?.lastName}
                                                  </Typography>
                                                  <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                    {member.user?.email}
                                                  </Typography>
                                                  <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                                    {getBudgetConsumedValue(member?.budget)} / {budgetLimit} used · {budgetRemaining} remaining
                                                  </Typography>
                                                </Box>
                                                <Chip
                                                  label={budgetStatus.label}
                                                  size="small"
                                                  color={budgetStatus.color}
                                                  variant="outlined"
                                                />
                                              </Stack>
                                            </Box>
                                          );
                                        })}
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                                </Box>

                                <Box
                                  sx={{
                                    border: "1px solid rgba(255, 255, 255, 0.12)",
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                                    overflow: "hidden",
                                  }}
                                >
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1.25,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" sx={billingSectionHeadingSx}>
                                    Projects closest to cap
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                    Review the most constrained project budgets without opening each editor row.
                                  </Typography>
                                </Box>
                                <IconButton
                                  aria-label={
                                    isUsageBudgetSectionExpanded(organization._id, "dashboard-projects")
                                      ? `Collapse project dashboard for ${organization.name}`
                                      : `Expand project dashboard for ${organization.name}`
                                  }
                                  onClick={() =>
                                    toggleUsageBudgetSectionExpanded(
                                      organization._id,
                                      "dashboard-projects",
                                    )
                                  }
                                  size="small"
                                  sx={nestedCollapseToggleSx}
                                >
                                  {isUsageBudgetSectionExpanded(organization._id, "dashboard-projects") ? (
                                    <ExpandLessOutlined />
                                  ) : (
                                    <ExpandMoreOutlined />
                                  )}
                                </IconButton>
                              </Box>
                              <Collapse
                                in={isUsageBudgetSectionExpanded(organization._id, "dashboard-projects")}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ px: 1.5, pb: 1.5 }}>
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", mb: 1.25 }}>
                                    {[
                                      { key: "all", label: "All capped" },
                                      { key: "near-cap", label: "Near cap" },
                                      { key: "at-cap", label: "At cap" },
                                    ].map((option) => (
                                      <Button
                                        key={`project-filter-${option.key}`}
                                        variant={projectDashboardFilter === option.key ? "contained" : "outlined"}
                                        onClick={() =>
                                          setProjectDashboardFilters((current) => ({
                                            ...current,
                                            [organization._id]: option.key,
                                          }))
                                        }
                                        sx={projectDashboardFilter === option.key ? actionButtonSx : outlinedActionButtonSx}
                                      >
                                        {option.label}
                                      </Button>
                                    ))}
                                  </Stack>
                                  {filteredProjectDashboardItems.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      {cappedProjectBudgets.length === 0
                                        ? "No capped projects yet."
                                        : "No projects match the current dashboard filter."}
                                    </Typography>
                                  ) : (
                                    <Box sx={{ maxHeight: { xl: 428 }, overflowY: "auto", pr: 0.5 }}>
                                      <Stack spacing={1}>
                                        {filteredProjectDashboardItems.slice(0, 5).map((project) => {
                                          const budgetStatus = getBudgetStatus(project?.budget);
                                          const budgetLimit = getBudgetLimitValue(project?.budget);
                                          const budgetRemaining = getBudgetRemainingValue(project?.budget);

                                          return (
                                            <Box
                                              key={`dashboard-project-${project._id}`}
                                              sx={{
                                                border: "1px solid rgba(255, 255, 255, 0.1)",
                                                borderRadius: 2,
                                                px: 1.5,
                                                py: 1.25,
                                                backgroundColor: "rgba(112, 118, 136, 0.24)",
                                              }}
                                            >
                                              <Stack
                                                direction={{ xs: "column", md: "row" }}
                                                spacing={1}
                                                justifyContent="space-between"
                                                alignItems={{ xs: "flex-start", md: "center" }}
                                              >
                                                <Box>
                                                  <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                                    {project.name}
                                                  </Typography>
                                                  <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                    {project.type}{project.draft ? " · Draft" : ""}
                                                  </Typography>
                                                  <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                                    {getBudgetConsumedValue(project?.budget)} / {budgetLimit} used · {budgetRemaining} remaining
                                                  </Typography>
                                                </Box>
                                                <Chip
                                                  label={budgetStatus.label}
                                                  size="small"
                                                  color={budgetStatus.color}
                                                  variant="outlined"
                                                />
                                              </Stack>
                                            </Box>
                                          );
                                        })}
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              </Collapse>
                                </Box>
                              </Box>
                            </Collapse>
                          </Box>
                          ) : null}

                          {activeBillingTab === "billing" ? (
                            <>
                              {renderOrganizationCollapsibleSection({
                                organizationId: organization._id,
                                organizationName: organization.name,
                                sectionKey: "billing-credit-packs",
                                title: "Credit packs",
                                description: "Purchase additional simulation credits outside the plan cycle.",
                                children: (
                                  <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25}>
                                    {ORG_CREDIT_PACK_OPTIONS.map((packOption) => {
                                      const purchaseKey = `${organization._id}:buy-credits:${packOption.key}`;

                                      return (
                                        <Box
                                          key={packOption.key}
                                          sx={{
                                            flex: 1,
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.4,
                                            backgroundColor: "rgba(14, 24, 64, 0.82)",
                                            border: "1px solid rgba(255, 255, 255, 0.08)",
                                          }}
                                        >
                                          <Stack spacing={1}>
                                            <Box>
                                              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                                {packOption.name}
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                {packOption.price}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                                {packOption.creditsLabel}
                                              </Typography>
                                            </Box>
                                            <Button
                                              variant="contained"
                                              onClick={() =>
                                                handleStartOrganizationCreditPurchase(
                                                  organization,
                                                  packOption.key,
                                                )
                                              }
                                              disabled={billingActionKey === purchaseKey}
                                              sx={actionButtonSx}
                                            >
                                              {billingActionKey === purchaseKey
                                                ? "Processing..."
                                                : "Buy credits"}
                                            </Button>
                                          </Stack>
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                ),
                              })}

                              {renderOrganizationCollapsibleSection({
                                organizationId: organization._id,
                                organizationName: organization.name,
                                sectionKey: "billing-status-balances",
                                title: "Status & balances",
                                description: "Review current billing status and available credit balances.",
                                children: (
                                  <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.25}
                                    useFlexGap
                                    sx={{ flexWrap: "wrap" }}
                                  >
                                    <Box
                                      sx={{
                                        minWidth: { xs: "100%", md: 180 },
                                        flex: 1,
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1.25,
                                        backgroundColor: "rgba(14, 24, 64, 0.82)",
                                        border: "1px solid rgba(255, 255, 255, 0.08)",
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                        Billing status
                                      </Typography>
                                      <Box sx={{ mt: 0.75 }}>
                                        <Chip
                                          label={
                                            BILLING_STATUS_LABELS[
                                              billingByOrganizationId[organization._id]?.billingStatus
                                            ] ||
                                            billingByOrganizationId[organization._id]?.billingStatus ||
                                            "Unconfigured"
                                          }
                                          size="small"
                                          color={getBillingStatusChipColor(
                                            billingByOrganizationId[organization._id]?.billingStatus,
                                          )}
                                          variant="outlined"
                                        />
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        minWidth: { xs: "100%", md: 180 },
                                        flex: 1,
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1.25,
                                        backgroundColor: "rgba(14, 24, 64, 0.82)",
                                        border: "1px solid rgba(255, 255, 255, 0.08)",
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                        Plan credits
                                      </Typography>
                                      <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                        {billingByOrganizationId[organization._id]?.planCredits ?? 0}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        minWidth: { xs: "100%", md: 180 },
                                        flex: 1,
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1.25,
                                        backgroundColor: "rgba(14, 24, 64, 0.82)",
                                        border: "1px solid rgba(255, 255, 255, 0.08)",
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                        Simulation credits
                                      </Typography>
                                      <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                        {billingByOrganizationId[organization._id]?.credits ?? 0}
                                      </Typography>
                                    </Box>
                                    {/* Desktop licence — its own entitlement, separate from credits
                                        (docs/desktop-license-entitlement-plan.md §6.1). Granted by PGL,
                                        so shown read-only with a way to ask for one. This surface is
                                        genuinely dark: LIGHT ink, per CLAUDE.md. */}
                                    <Box
                                      data-testid="org-desktop-license"
                                      sx={{
                                        minWidth: { xs: "100%", md: 180 },
                                        flex: 1,
                                        borderRadius: 2,
                                        px: 1.5,
                                        py: 1.25,
                                        backgroundColor: "rgba(14, 24, 64, 0.82)",
                                        border: "1px solid rgba(255, 255, 255, 0.08)",
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                        Desktop licence
                                      </Typography>
                                      {(() => {
                                        const lic = billingByOrganizationId[organization._id]?.desktopLicense;
                                        const until = lic?.expiresAt ? new Date(lic.expiresAt).toLocaleDateString() : null;
                                        if (lic?.status === "active") {
                                          return (
                                            <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                              Active until {until}
                                            </Typography>
                                          );
                                        }
                                        return (
                                          <>
                                            <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                              {lic?.status === "expired" ? `Expired ${until}` : "None"}
                                            </Typography>
                                            <Typography
                                              component="a"
                                              href="mailto:contact@greenleaves.app?subject=CityDigitalTwin%20Desktop%20license"
                                              variant="caption"
                                              sx={{ color: "#7ff0b6" }}
                                            >
                                              {lic?.status === "expired" ? "Renew — contact us" : "Get one — contact us"}
                                            </Typography>
                                          </>
                                        );
                                      })()}
                                    </Box>
                                  </Stack>
                                ),
                              })}

                              {renderOrganizationCollapsibleSection({
                                organizationId: organization._id,
                                organizationName: organization.name,
                                sectionKey: "billing-profile",
                                title: "Billing profile",
                                description: "Set the organization billing contact, name, address, and tax ID (shown on invoices).",
                                children: (
                                  <Box
                                    component="form"
                                    onSubmit={(event) => {
                                      event.preventDefault();
                                      handleBillingProfileSave(organization);
                                    }}
                                    sx={{
                                      display: "grid",
                                      gap: 1.5,
                                    }}
                                  >
                                    <TextField
                                      label="Billing email"
                                      type="email"
                                      value={billingProfileDrafts[organization._id]?.billingEmail || ""}
                                      onChange={(event) =>
                                        handleBillingDraftChange(
                                          organization._id,
                                          "billingEmail",
                                          event.target.value,
                                        )
                                      }
                                      disabled={billingSavingOrgId === organization._id}
                                      fullWidth
                                      sx={darkInputSx}
                                    />
                                    <TextField
                                      label="Billing name"
                                      value={billingProfileDrafts[organization._id]?.billingName || ""}
                                      onChange={(event) =>
                                        handleBillingDraftChange(
                                          organization._id,
                                          "billingName",
                                          event.target.value,
                                        )
                                      }
                                      disabled={billingSavingOrgId === organization._id}
                                      fullWidth
                                      sx={darkInputSx}
                                    />
                                    <TextField
                                      label="Billing currency"
                                      value={(billingProfileDrafts[organization._id]?.billingCurrency || "cad").toUpperCase()}
                                      disabled
                                      fullWidth
                                      helperText="Only CAD is supported right now."
                                      sx={darkInputSx}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#94a3b8", display: "block", mt: 0.5 }}
                                    >
                                      Billing address &amp; tax ID (shown on invoices)
                                    </Typography>
                                    <TextField
                                      label="Address line 1"
                                      value={billingProfileDrafts[organization._id]?.billingAddress?.line1 || ""}
                                      onChange={(event) =>
                                        handleBillingAddressChange(organization._id, "line1", event.target.value)
                                      }
                                      disabled={billingSavingOrgId === organization._id}
                                      fullWidth
                                      sx={darkInputSx}
                                    />
                                    <TextField
                                      label="Address line 2"
                                      value={billingProfileDrafts[organization._id]?.billingAddress?.line2 || ""}
                                      onChange={(event) =>
                                        handleBillingAddressChange(organization._id, "line2", event.target.value)
                                      }
                                      disabled={billingSavingOrgId === organization._id}
                                      fullWidth
                                      sx={darkInputSx}
                                    />
                                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                                      <TextField
                                        label="City"
                                        value={billingProfileDrafts[organization._id]?.billingAddress?.city || ""}
                                        onChange={(event) =>
                                          handleBillingAddressChange(organization._id, "city", event.target.value)
                                        }
                                        disabled={billingSavingOrgId === organization._id}
                                        fullWidth
                                        sx={darkInputSx}
                                      />
                                      <TextField
                                        label="Province / state"
                                        value={billingProfileDrafts[organization._id]?.billingAddress?.state || ""}
                                        onChange={(event) =>
                                          handleBillingAddressChange(organization._id, "state", event.target.value)
                                        }
                                        disabled={billingSavingOrgId === organization._id}
                                        fullWidth
                                        sx={darkInputSx}
                                      />
                                      <TextField
                                        label="Postal code"
                                        value={billingProfileDrafts[organization._id]?.billingAddress?.postalCode || ""}
                                        onChange={(event) =>
                                          handleBillingAddressChange(organization._id, "postalCode", event.target.value)
                                        }
                                        disabled={billingSavingOrgId === organization._id}
                                        fullWidth
                                        sx={darkInputSx}
                                      />
                                      <TextField
                                        label="Country (ISO-2, e.g. CA)"
                                        value={billingProfileDrafts[organization._id]?.billingAddress?.country || ""}
                                        onChange={(event) =>
                                          handleBillingAddressChange(organization._id, "country", event.target.value.toUpperCase())
                                        }
                                        disabled={billingSavingOrgId === organization._id}
                                        fullWidth
                                        inputProps={{ maxLength: 2 }}
                                        sx={darkInputSx}
                                      />
                                    </Box>
                                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" }, gap: 1.5 }}>
                                      <TextField
                                        label="Tax ID type"
                                        placeholder="e.g. ca_gst_hst"
                                        value={billingProfileDrafts[organization._id]?.taxIdType || ""}
                                        onChange={(event) =>
                                          handleBillingDraftChange(organization._id, "taxIdType", event.target.value)
                                        }
                                        disabled={billingSavingOrgId === organization._id}
                                        fullWidth
                                        sx={darkInputSx}
                                      />
                                      <TextField
                                        label="Tax ID number"
                                        value={billingProfileDrafts[organization._id]?.taxIdValue || ""}
                                        onChange={(event) =>
                                          handleBillingDraftChange(organization._id, "taxIdValue", event.target.value)
                                        }
                                        disabled={billingSavingOrgId === organization._id}
                                        fullWidth
                                        helperText="Leave both blank to remove the tax ID."
                                        sx={darkInputSx}
                                      />
                                    </Box>
                                    <Box>
                                      <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={billingSavingOrgId === organization._id}
                                        sx={actionButtonSx}
                                      >
                                        {billingSavingOrgId === organization._id
                                          ? "Saving..."
                                          : "Save billing profile"}
                                      </Button>
                                    </Box>
                                  </Box>
                                ),
                              })}

                              {renderOrganizationCollapsibleSection({
                                organizationId: organization._id,
                                organizationName: organization.name,
                                sectionKey: "billing-history",
                                title: "Billing history",
                                description: "Download your invoices and receipts.",
                                children: (
                                  <Stack spacing={1}>
                                    {!organizationInvoices[organization._id] ? (
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                        Loading billing history…
                                      </Typography>
                                    ) : organizationInvoices[organization._id].length === 0 ? (
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                        No invoices or receipts yet.
                                      </Typography>
                                    ) : (
                                      organizationInvoices[organization._id].map((entry) => (
                                        <Box
                                          key={entry.id}
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1,
                                            backgroundColor: "rgba(14, 24, 64, 0.82)",
                                            border: "1px solid rgba(255, 255, 255, 0.08)",
                                          }}
                                        >
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ color: "#f8fbff" }}>
                                              {entry.currency} {Number(entry.amount || 0).toFixed(2)}
                                              {entry.number ? ` · ${entry.number}` : ""}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                              {new Date(entry.created * 1000).toLocaleDateString()} · {entry.description} · {entry.status}
                                            </Typography>
                                          </Box>
                                          {entry.pdfUrl || entry.hostedUrl ? (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              component="a"
                                              href={entry.pdfUrl || entry.hostedUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={outlinedActionButtonSx}
                                            >
                                              {entry.pdfUrl ? "PDF" : "Receipt"}
                                            </Button>
                                          ) : null}
                                        </Box>
                                      ))
                                    )}
                                  </Stack>
                                ),
                              })}

                              {renderOrganizationCollapsibleSection({
                                organizationId: organization._id,
                                organizationName: organization.name,
                                sectionKey: "billing-summary",
                                title: "Subscription summary",
                                description: "See the current subscription state and payment defaults at a glance.",
                                children: (
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gap: 1,
                                      alignContent: "start",
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      Plan: {billingByOrganizationId[organization._id]?.plan || "None yet"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      Subscription status: {billingByOrganizationId[organization._id]?.subscriptionStatus || "Not connected"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      Subscription expires: {formatDateTime(
                                        billingByOrganizationId[organization._id]?.subscriptionExpiresAt,
                                      )}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      Pending plan: {billingByOrganizationId[organization._id]?.pendingPlan || "None"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      Cancel at period end: {billingByOrganizationId[organization._id]?.cancelAtPeriodEnd ? "Yes" : "No"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      Stripe customer: {billingByOrganizationId[organization._id]?.hasStripeCustomer ? "Configured" : "Not configured"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      Default payment method:{" "}
                                      {billingByOrganizationId[organization._id]?.defaultPaymentMethod
                                        ? `${billingByOrganizationId[organization._id]?.defaultPaymentMethod?.brand || "card"} ending in ${billingByOrganizationId[organization._id]?.defaultPaymentMethod?.last4 || "----"}`
                                        : "None"}
                                    </Typography>
                                  </Box>
                                ),
                              })}
                            </>
                          ) : null}

                          {activeBillingTab === "usage" ? (
                          <Box
                            sx={{
                              border: "1px solid rgba(255, 255, 255, 0.12)",
                              borderRadius: 2,
                              backgroundColor: "rgba(255, 255, 255, 0.03)",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.5,
                                py: 1.25,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={billingSectionHeadingSx}>
                                  Usage budgets and recent activity
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  Set optional simulation caps per member or project, then review recent grouped usage activity.
                                </Typography>
                              </Box>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                {renderHelpButton("usage-overview", "Usage budgets and recent activity")}
                                <IconButton
                                  aria-label={
                                    isUsageBudgetSectionExpanded(organization._id, "usage-overview")
                                      ? `Collapse usage budgets and recent activity for ${organization.name}`
                                      : `Expand usage budgets and recent activity for ${organization.name}`
                                  }
                                  onClick={() =>
                                    toggleUsageBudgetSectionExpanded(organization._id, "usage-overview")
                                  }
                                  size="small"
                                  sx={sectionCollapseToggleSx}
                                >
                                  {isUsageBudgetSectionExpanded(organization._id, "usage-overview") ? (
                                    <ExpandLessOutlined />
                                  ) : (
                                    <ExpandMoreOutlined />
                                  )}
                                </IconButton>
                              </Stack>
                            </Box>
                            <Collapse
                              in={isUsageBudgetSectionExpanded(organization._id, "usage-overview")}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ px: 1.5, pb: 1.5, display: "grid", gap: 1.25 }}>
                                <Box
                                  sx={{
                                    border: "1px solid rgba(255, 255, 255, 0.12)",
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 1.25,
                                    backgroundColor: "rgba(14, 24, 64, 0.82)",
                                  }}
                                >
                                  <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.25}
                                    useFlexGap
                                    sx={{ flexWrap: "wrap" }}
                                  >
                                    <Box sx={{ minWidth: { xs: "100%", md: 180 }, flex: 1 }}>
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                        Available plan credits
                                      </Typography>
                                      <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                        {billingByOrganizationId[organization._id]?.planCredits ?? 0}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ minWidth: { xs: "100%", md: 180 }, flex: 1 }}>
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                        Available simulation credits
                                      </Typography>
                                      <Typography variant="h6" sx={{ color: "#f8fbff", mt: 0.25 }}>
                                        {billingByOrganizationId[organization._id]?.credits ?? 0}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ minWidth: { xs: "100%", md: 240 }, flex: 1.4 }}>
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                        Reference only
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)", mt: 0.3 }}>
                                        These balances help owners estimate member and project caps. They do not add any extra limit behavior beyond the budget caps you set below.
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </Box>

                                {billingBudgetsLoadingByOrganizationId[organization._id] ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">Loading usage budgets...</Typography>
                                  </Box>
                                ) : (
                                  <Stack spacing={1.5}>
                                <Box
                                  sx={{
                                    border: "1px solid rgba(255, 255, 255, 0.12)",
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 1.5,
                                      py: 1.25,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body2" sx={billingSectionHeadingSx}>
                                        Member budgets
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                        {editableMemberBudgets.length} active members
                                      </Typography>
                                    </Box>
                                    <IconButton
                                      aria-label={
                                        isUsageBudgetSectionExpanded(organization._id, "members")
                                          ? `Collapse member budgets for ${organization.name}`
                                          : `Expand member budgets for ${organization.name}`
                                      }
                                      onClick={() => toggleUsageBudgetSectionExpanded(organization._id, "members")}
                                      size="small"
                                      sx={nestedCollapseToggleSx}
                                    >
                                      {isUsageBudgetSectionExpanded(organization._id, "members") ? (
                                        <ExpandLessOutlined />
                                      ) : (
                                        <ExpandMoreOutlined />
                                      )}
                                    </IconButton>
                                  </Box>
                                  <Collapse
                                    in={isUsageBudgetSectionExpanded(organization._id, "members")}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box sx={{ px: 1.5, pb: 1.5 }}>
                                      {editableMemberBudgets.length === 0 ? (
                                        <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                          No active members available for budget controls yet.
                                        </Typography>
                                      ) : (
                                        <Box sx={{ maxHeight: 472, overflowY: "auto", pr: 0.5 }}>
                                          <Stack spacing={1}>
                                            {editableMemberBudgets.map((member) => {
                                              const actionKey = `${organization._id}:${member._id}:member-budget`;
                                              const resetActionKey = `${organization._id}:${member._id}:member-budget-reset`;
                                              const draft = getMemberBudgetDraft(
                                                memberBudgetDrafts[member._id],
                                                member,
                                              );

                                              return (
                                                <Box
                                                  key={member._id}
                                                  sx={{
                                                    border: "1px solid rgba(255, 255, 255, 0.1)",
                                                    borderRadius: 2,
                                                    px: 1.5,
                                                    py: 1.25,
                                                    backgroundColor: "rgba(14, 24, 64, 0.82)",
                                                  }}
                                                >
                                                  <Stack spacing={1.25}>
                                                    <Box>
                                                      <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                                        {member.user?.firstName} {member.user?.lastName}
                                                      </Typography>
                                                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                        {member.user?.email} · {ORG_ROLE_LABELS[member.orgRole] || member.orgRole}
                                                      </Typography>
                                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                                        Consumed: {member?.budget?.consumed ?? 0} · Remaining: {formatBudgetValue(member?.budget?.remaining)}
                                                      </Typography>
                                                      <Typography variant="caption" sx={{ display: "block", color: "rgba(223, 229, 255, 0.68)" }}>
                                                        {formatMemberBudgetResetSummary(member)}
                                                      </Typography>
                                                      {member?.budgetReset?.lastResetAt ? (
                                                        <Typography variant="caption" sx={{ display: "block", color: "rgba(223, 229, 255, 0.58)" }}>
                                                          Last reset: {formatDateTime(member.budgetReset.lastResetAt)}
                                                        </Typography>
                                                      ) : null}
                                                    </Box>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                                      <TextField
                                                        label="Simulation cap"
                                                        type="number"
                                                        value={draft.limit}
                                                        onChange={(event) =>
                                                          setMemberBudgetDrafts((current) => ({
                                                            ...current,
                                                            [member._id]: {
                                                              ...getMemberBudgetDraft(current[member._id], member),
                                                              limit: event.target.value,
                                                            },
                                                          }))
                                                        }
                                                        fullWidth
                                                        helperText="0 blocks all runs · leave empty for no cap"
                                                        inputProps={{ min: 0 }}
                                                        sx={budgetCardInputSx}
                                                      />
                                                      <TextField
                                                        label="Every"
                                                        type="number"
                                                        value={draft.resetIntervalCount}
                                                        onChange={(event) =>
                                                          setMemberBudgetDrafts((current) => ({
                                                            ...current,
                                                            [member._id]: {
                                                              ...getMemberBudgetDraft(current[member._id], member),
                                                              resetIntervalCount: event.target.value,
                                                            },
                                                          }))
                                                        }
                                                        fullWidth
                                                        inputProps={{ min: 1 }}
                                                        sx={budgetCardInputSx}
                                                      />
                                                      <FormControl fullWidth sx={budgetCardInputSx}>
                                                        <InputLabel id={`member-reset-unit-${member._id}`}>
                                                          Unit
                                                        </InputLabel>
                                                        <Select
                                                          labelId={`member-reset-unit-${member._id}`}
                                                          label="Unit"
                                                          value={draft.resetIntervalUnit}
                                                          onChange={(event) =>
                                                            setMemberBudgetDrafts((current) => ({
                                                              ...current,
                                                              [member._id]: {
                                                                ...getMemberBudgetDraft(current[member._id], member),
                                                                resetIntervalUnit: event.target.value,
                                                              },
                                                            }))
                                                          }
                                                        >
                                                          <MenuItem value="">Off</MenuItem>
                                                          <MenuItem value="week">Week</MenuItem>
                                                          <MenuItem value="month">Month</MenuItem>
                                                        </Select>
                                                      </FormControl>
                                                      <TextField
                                                        label="Period start"
                                                        type="date"
                                                        value={draft.resetStartAt}
                                                        onChange={(event) =>
                                                          setMemberBudgetDrafts((current) => ({
                                                            ...current,
                                                            [member._id]: {
                                                              ...getMemberBudgetDraft(current[member._id], member),
                                                              resetStartAt: event.target.value,
                                                            },
                                                          }))
                                                        }
                                                        fullWidth
                                                        InputLabelProps={{ shrink: true }}
                                                        sx={budgetCardInputSx}
                                                      />
                                                    </Stack>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                                      <Button
                                                        variant="contained"
                                                        onClick={() => handleMemberBudgetSave(organization, member)}
                                                        disabled={
                                                          billingActionKey === actionKey ||
                                                          isMemberBudgetDraftEqual(draft, member)
                                                        }
                                                        sx={actionButtonSx}
                                                      >
                                                        {billingActionKey === actionKey ? "Saving..." : "Save cap"}
                                                      </Button>
                                                      <Button
                                                        variant="outlined"
                                                        onClick={() => handleMemberBudgetReset(organization, member)}
                                                        disabled={
                                                          billingActionKey === resetActionKey ||
                                                          Number(member?.budget?.consumed ?? 0) <= 0
                                                        }
                                                        sx={outlinedActionButtonSx}
                                                      >
                                                        {billingActionKey === resetActionKey ? "Resetting..." : "Reset used"}
                                                      </Button>
                                                    </Stack>
                                                  </Stack>
                                                </Box>
                                              );
                                            })}
                                          </Stack>
                                        </Box>
                                      )}
                                    </Box>
                                  </Collapse>
                                </Box>

                                <Box
                                  sx={{
                                    border: "1px solid rgba(255, 255, 255, 0.12)",
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 1.5,
                                      py: 1.25,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body2" sx={billingSectionHeadingSx}>
                                        Project budgets
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                        {organizationBillingBudgets.projects.length} organization projects
                                      </Typography>
                                    </Box>
                                    <IconButton
                                      aria-label={
                                        isUsageBudgetSectionExpanded(organization._id, "projects")
                                          ? `Collapse project budgets for ${organization.name}`
                                          : `Expand project budgets for ${organization.name}`
                                      }
                                      onClick={() => toggleUsageBudgetSectionExpanded(organization._id, "projects")}
                                      size="small"
                                      sx={nestedCollapseToggleSx}
                                    >
                                      {isUsageBudgetSectionExpanded(organization._id, "projects") ? (
                                        <ExpandLessOutlined />
                                      ) : (
                                        <ExpandMoreOutlined />
                                      )}
                                    </IconButton>
                                  </Box>
                                  <Collapse
                                    in={isUsageBudgetSectionExpanded(organization._id, "projects")}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box sx={{ px: 1.5, pb: 1.5 }}>
                                      {organizationBillingBudgets.projects.length === 0 ? (
                                        <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                          No organization projects available for budget controls yet.
                                        </Typography>
                                      ) : (
                                        <Box sx={{ maxHeight: 472, overflowY: "auto", pr: 0.5 }}>
                                          <Stack spacing={1}>
                                            {organizationBillingBudgets.projects.map((project) => {
                                              const actionKey = `${organization._id}:${project._id}:project-budget`;
                                              const draft =
                                                projectBudgetDrafts[project._id] ??
                                                toBudgetDraftValue(project?.budget?.limit);

                                              return (
                                                <Box
                                                  key={project._id}
                                                  sx={{
                                                    border: "1px solid rgba(255, 255, 255, 0.1)",
                                                    borderRadius: 2,
                                                    px: 1.5,
                                                    py: 1.25,
                                                    backgroundColor: "rgba(14, 24, 64, 0.82)",
                                                  }}
                                                >
                                                  <Stack spacing={1.25}>
                                                    <Box>
                                                      <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                                        {project.name}
                                                      </Typography>
                                                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                        {project.type}
                                                        {project.draft ? " · Draft" : ""}
                                                      </Typography>
                                                      <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                                        Consumed: {project?.budget?.consumed ?? 0} · Remaining: {formatBudgetValue(project?.budget?.remaining)}
                                                      </Typography>
                                                    </Box>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                                      <TextField
                                                        label="Simulation cap"
                                                        type="number"
                                                        value={draft}
                                                        onChange={(event) =>
                                                          setProjectBudgetDrafts((current) => ({
                                                            ...current,
                                                            [project._id]: event.target.value,
                                                          }))
                                                        }
                                                        fullWidth
                                                        helperText="0 blocks all runs · leave empty for no cap"
                                                        inputProps={{ min: 0 }}
                                                        sx={budgetCardInputSx}
                                                      />
                                                      <Button
                                                        variant="contained"
                                                        onClick={() => handleProjectBudgetSave(organization, project)}
                                                        disabled={
                                                          billingActionKey === actionKey ||
                                                          draft === toBudgetDraftValue(project?.budget?.limit)
                                                        }
                                                        sx={{
                                                          ...actionButtonSx,
                                                          minWidth: 112,
                                                          whiteSpace: "nowrap",
                                                        }}
                                                      >
                                                        {billingActionKey === actionKey ? "Saving..." : "Save cap"}
                                                      </Button>
                                                    </Stack>
                                                  </Stack>
                                                </Box>
                                              );
                                            })}
                                          </Stack>
                                        </Box>
                                      )}
                                    </Box>
                                  </Collapse>
                                </Box>

                                <Box sx={{ display: "grid", gap: 1.25 }}>
                                  <Typography variant="body2" sx={billingSectionHeadingSx}>
                                    Recent usage activity
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                    Recent grouped organization usage debits across members, projects, and funding sources.
                                  </Typography>
                                  {usageActivityGroups.length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      No recent usage activity yet.
                                    </Typography>
                                  ) : (
                                    <Stack spacing={1}>
                                      {usageActivityGroups.map((activityGroup) => (
                                        <Box
                                          key={`usage-activity-${activityGroup.key}`}
                                          sx={{
                                            border: "1px solid rgba(255, 255, 255, 0.1)",
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            backgroundColor: "rgba(112, 118, 136, 0.24)",
                                          }}
                                        >
                                          <Stack
                                            direction={{ xs: "column", md: "row" }}
                                            spacing={1}
                                            justifyContent="space-between"
                                            alignItems={{ xs: "flex-start", md: "center" }}
                                          >
                                            <Box>
                                              <Typography variant="body2" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                                {formatUsageFundingSource(activityGroup.fundingSource)}
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                {formatUsageActivityLabel(activityGroup.actorName, "Unknown member")} · {formatUsageActivityLabel(activityGroup.projectName, "Unscoped project")}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                                {activityGroup.debitCount} debit{activityGroup.debitCount === 1 ? "" : "s"} · {activityGroup.totalAmount} simulation{activityGroup.totalAmount === 1 ? "" : "s"} consumed
                                              </Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                              {formatDateTime(activityGroup.latestAt)}
                                            </Typography>
                                          </Stack>
                                        </Box>
                                      ))}
                                    </Stack>
                                  )}
                                </Box>
                                  </Stack>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                          ) : null}

                          <Stack spacing={2}>
                            {activeBillingTab === "usage"
                              ? renderOrganizationCollapsibleSection({
                                  organizationId: organization._id,
                                  organizationName: organization.name,
                                  sectionKey: "usage-ledger",
                                  title: "Ledger",
                                  description: "Review each billing entry applied to the organization account.",
                                  children: billingLedgerLoadingByOrganizationId[organization._id] ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                      <CircularProgress size={18} />
                                      <Typography variant="body2">Loading ledger...</Typography>
                                    </Box>
                                  ) : (billingLedgerByOrganizationId[organization._id] || []).length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      No billing ledger entries yet.
                                    </Typography>
                                  ) : (
                                    <Stack spacing={1}>
                                      {(billingLedgerByOrganizationId[organization._id] || []).map((item) => (
                                        <Box
                                          key={item._id}
                                          sx={{
                                            border: "1px solid rgba(255, 255, 255, 0.1)",
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            backgroundColor: "rgba(112, 118, 136, 0.24)",
                                          }}
                                        >
                                          <Stack
                                            direction={{ xs: "column", md: "row" }}
                                            spacing={1}
                                            justifyContent="space-between"
                                          >
                                            <Box>
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#f8fbff" }}>
                                                {String(item.entryType || "entry")
                                                  .replace(/_/g, " ")
                                                  .replace(/\b\w/g, (char) => char.toUpperCase())}
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                                {item.description || "No description yet."}
                                              </Typography>
                                            </Box>
                                            <Box>
                                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#c9ffdf" }}>
                                                {formatSignedAmount(item.amount)}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                                {formatDateTime(item.createdAt)}
                                              </Typography>
                                            </Box>
                                          </Stack>
                                        </Box>
                                      ))}
                                    </Stack>
                                  ),
                                })
                              : null}

                            {activeBillingTab === "usage"
                              ? renderOrganizationCollapsibleSection({
                                  organizationId: organization._id,
                                  organizationName: organization.name,
                                  sectionKey: "usage-plan-credit-cycles",
                                  title: "Plan credit cycles",
                                  description: "Compare granted, consumed, and remaining plan credits by subscription period.",
                                  children: billingCyclesLoadingByOrganizationId[organization._id] ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                      <CircularProgress size={18} />
                                      <Typography variant="body2">Loading plan credit cycles...</Typography>
                                    </Box>
                                  ) : (billingCyclesByOrganizationId[organization._id] || []).length === 0 ? (
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      No plan credit cycles yet.
                                    </Typography>
                                  ) : (
                                    <Stack spacing={1}>
                                      {(billingCyclesByOrganizationId[organization._id] || []).map((item) => (
                                        <Box
                                          key={item._id}
                                          sx={{
                                            border: "1px solid rgba(255, 255, 255, 0.1)",
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            backgroundColor: "rgba(112, 118, 136, 0.24)",
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#f8fbff" }}>
                                            {formatDateTime(item.periodStart)} to {formatDateTime(item.periodEnd)}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                            Granted: {item.grantedPlanCredits ?? 0} · Consumed: {item.consumedPlanCredits ?? 0} · Remaining: {item.remainingPlanCredits ?? 0}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  ),
                                })
                              : null}
                          </Stack>
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                          Billing data is not available yet.
                        </Typography>
                      )}
                    </Box>
                      ) : null}

                      {activeTab === "identity" &&
                      organization.membership?.orgRole === "org_owner" &&
                      ENTERPRISE_SSO_ENABLED ? (
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.25}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography sx={billingTabHeadingSx}>
                                  Enterprise login
                                </Typography>
                                {renderHelpButton("org-identity", "Single sign-on")}
                              </Stack>
                              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                Review the current organization identity-provider configuration before setup and live OIDC rollout.
                              </Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              onClick={() => refreshOrganizationIdentityProviders(organization._id)}
                              disabled={identityProvidersLoading}
                              sx={outlinedActionButtonSx}
                            >
                              {identityProvidersLoading ? "Refreshing..." : "Refresh identity"}
                            </Button>
                          </Stack>

                          {identityProvidersLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <CircularProgress size={18} />
                              <Typography variant="body2">Loading identity providers...</Typography>
                            </Box>
                          ) : identityProviders.length === 0 ? (
                            <Box sx={billingSectionContainerSx}>
                              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                                No enterprise identity providers are configured yet.
                              </Typography>
                              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.68)", mt: 0.75 }}>
                                Save a provider below to start the OIDC configuration flow for this organization.
                              </Typography>
                            </Box>
                          ) : (
                            <Stack spacing={1.5}>
                              {identityProviders.map((provider) => (
                                <Box
                                  key={provider.providerType}
                                  sx={{
                                    ...billingSectionContainerSx,
                                    display: "grid",
                                    gap: 1.25,
                                  }}
                                >
                                  <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                  >
                                    <Box>
                                      <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                                        {IDENTITY_PROVIDER_TYPE_LABELS[provider.providerType] || provider.providerType}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                                        {provider.displayName || "No display name configured"}
                                      </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                                      <Chip
                                        label={IDENTITY_PROVIDER_STATUS_LABELS[provider.status] || provider.status}
                                        size="small"
                                        color={provider.status === "active" ? "success" : provider.status === "draft" ? "warning" : "default"}
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={
                                          IDENTITY_PROVIDER_VALIDATION_LABELS[provider.lastValidationStatus] ||
                                          provider.lastValidationStatus ||
                                          "Not validated"
                                        }
                                        size="small"
                                        color={provider.lastValidationStatus === "valid" ? "success" : provider.lastValidationStatus === "invalid" ? "error" : "default"}
                                        variant="outlined"
                                      />
                                    </Stack>
                                  </Stack>

                                  <Stack spacing={0.5}>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                                      Issuer: {provider.issuer || "-"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                                      Client ID: {provider.clientId || "-"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                                      Redirect URIs: {provider.redirectUris?.length || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                                      Allowed email domains: {provider.allowedEmailDomains?.length ? provider.allowedEmailDomains.join(", ") : "None configured"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                                      Local password login: {provider.enforceSso ? "Blocked for organization members" : "Allowed"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                                      Client secret: {provider.hasClientSecret ? "Configured" : "Missing"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.7)" }}>
                                      Last validated: {formatDateTime(provider.lastValidatedAt)}
                                    </Typography>
                                    {provider.lastValidationError ? (
                                      <Alert severity="warning" sx={{ mt: 0.75 }}>
                                        {provider.lastValidationError}
                                      </Alert>
                                    ) : null}
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          )}

                          <Box sx={billingSectionContainerSx}>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1.25}
                              justifyContent="space-between"
                              alignItems={{ xs: "flex-start", sm: "center" }}
                            >
                              <Box>
                                <Typography variant="body2" sx={billingSectionHeadingSx}>
                                  Configure provider
                                </Typography>
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  Save one provider configuration at a time. Client secrets are write-only and are never shown after save.
                                </Typography>
                              </Box>
                              <FormControl sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                                <InputLabel id={`identity-provider-type-${organization._id}`} sx={{ color: "rgba(223, 229, 255, 0.76)" }}>
                                  Provider
                                </InputLabel>
                                <Select
                                  labelId={`identity-provider-type-${organization._id}`}
                                  value={selectedIdentityProviderType}
                                  label="Provider"
                                  onChange={(event) =>
                                    handleIdentityProviderTypeChange(
                                      organization._id,
                                      event.target.value,
                                    )
                                  }
                                  sx={darkInputSx}
                                >
                                  {IDENTITY_PROVIDER_TYPE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>

                            <Stack spacing={1.5}>
                              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
                                <FormControl fullWidth>
                                  <InputLabel id={`identity-provider-status-${organization._id}`} sx={{ color: "rgba(223, 229, 255, 0.76)" }}>
                                    Status
                                  </InputLabel>
                                  <Select
                                    labelId={`identity-provider-status-${organization._id}`}
                                    value={identityProviderDraft.status}
                                    label="Status"
                                    onChange={(event) =>
                                      handleIdentityProviderDraftChange(
                                        organization._id,
                                        selectedIdentityProviderType,
                                        "status",
                                        event.target.value,
                                      )
                                    }
                                    sx={darkInputSx}
                                  >
                                    {Object.entries(IDENTITY_PROVIDER_STATUS_LABELS).map(([value, label]) => (
                                      <MenuItem key={value} value={value}>
                                        {label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  label="Display name"
                                  value={identityProviderDraft.displayName}
                                  onChange={(event) =>
                                    handleIdentityProviderDraftChange(
                                      organization._id,
                                      selectedIdentityProviderType,
                                      "displayName",
                                      event.target.value,
                                    )
                                  }
                                  fullWidth
                                  sx={darkInputSx}
                                />
                              </Stack>

                              <TextField
                                label="Issuer URL"
                                value={identityProviderDraft.issuer}
                                onChange={(event) =>
                                  handleIdentityProviderDraftChange(
                                    organization._id,
                                    selectedIdentityProviderType,
                                    "issuer",
                                    event.target.value,
                                  )
                                }
                                fullWidth
                                error={Boolean(getIdentityProviderFieldError("issuer"))}
                                helperText={getIdentityProviderFieldError("issuer")}
                                sx={darkInputSx}
                              />

                              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
                                <TextField
                                  label="Client ID"
                                  value={identityProviderDraft.clientId}
                                  onChange={(event) =>
                                    handleIdentityProviderDraftChange(
                                      organization._id,
                                      selectedIdentityProviderType,
                                      "clientId",
                                      event.target.value,
                                    )
                                  }
                                  fullWidth
                                  error={Boolean(getIdentityProviderFieldError("clientId"))}
                                  helperText={getIdentityProviderFieldError("clientId")}
                                  sx={darkInputSx}
                                />
                                <TextField
                                  label={selectedIdentityProvider?.hasClientSecret ? "Client secret (leave blank to keep current)" : "Client secret"}
                                  type="password"
                                  value={identityProviderDraft.clientSecret}
                                  onChange={(event) =>
                                    handleIdentityProviderDraftChange(
                                      organization._id,
                                      selectedIdentityProviderType,
                                      "clientSecret",
                                      event.target.value,
                                    )
                                  }
                                  fullWidth
                                  error={Boolean(getIdentityProviderFieldError("clientSecret"))}
                                  helperText={
                                    getIdentityProviderFieldError("clientSecret") ||
                                    (selectedIdentityProvider?.hasClientSecret
                                      ? "Leave blank to keep the current stored secret."
                                      : "Stored secrets are write-only and never shown after save.")
                                  }
                                  sx={darkInputSx}
                                />
                              </Stack>

                              <TextField
                                label="Discovery URL"
                                value={identityProviderDraft.discoveryUrl}
                                onChange={(event) =>
                                  handleIdentityProviderDraftChange(
                                    organization._id,
                                    selectedIdentityProviderType,
                                    "discoveryUrl",
                                    event.target.value,
                                  )
                                }
                                fullWidth
                                error={Boolean(getIdentityProviderFieldError("discoveryUrl"))}
                                helperText={getIdentityProviderFieldError("discoveryUrl")}
                                sx={darkInputSx}
                              />

                              <TextField
                                label="Redirect URIs"
                                value={identityProviderDraft.redirectUris}
                                onChange={(event) =>
                                  handleIdentityProviderDraftChange(
                                    organization._id,
                                    selectedIdentityProviderType,
                                    "redirectUris",
                                    event.target.value,
                                  )
                                }
                                helperText={
                                  getIdentityProviderFieldError("redirectUris") ||
                                  "One redirect URI per line."
                                }
                                fullWidth
                                multiline
                                minRows={2}
                                error={Boolean(getIdentityProviderFieldError("redirectUris"))}
                                sx={darkInputSx}
                              />

                              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
                                <TextField
                                  label="Allowed email domains"
                                  value={identityProviderDraft.allowedEmailDomains}
                                  onChange={(event) =>
                                    handleIdentityProviderDraftChange(
                                      organization._id,
                                      selectedIdentityProviderType,
                                      "allowedEmailDomains",
                                      event.target.value,
                                    )
                                  }
                                  helperText="Comma-separated domains."
                                  fullWidth
                                  sx={darkInputSx}
                                />
                                <TextField
                                  label="Scopes"
                                  value={identityProviderDraft.scopes}
                                  onChange={(event) =>
                                    handleIdentityProviderDraftChange(
                                      organization._id,
                                      selectedIdentityProviderType,
                                      "scopes",
                                      event.target.value,
                                    )
                                  }
                                  helperText="Comma-separated scopes."
                                  fullWidth
                                  sx={darkInputSx}
                                />
                              </Stack>

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={Boolean(identityProviderDraft.enforceSso)}
                                    onChange={(event) =>
                                      handleIdentityProviderDraftChange(
                                        organization._id,
                                        selectedIdentityProviderType,
                                        "enforceSso",
                                        event.target.checked,
                                      )
                                    }
                                    sx={{
                                      color: "rgba(223, 229, 255, 0.72)",
                                      "&.Mui-checked": {
                                        color: "#f8fafc",
                                      },
                                    }}
                                  />
                                }
                                label="Require organization SSO for local login"
                                sx={{ color: "rgba(223, 229, 255, 0.92)" }}
                              />

                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                                <Button
                                  variant="contained"
                                  onClick={() => handleSaveIdentityProvider(organization)}
                                  disabled={billingActionKey === `${organization._id}:identity-save:${selectedIdentityProviderType}`}
                                  sx={actionButtonSx}
                                >
                                  {billingActionKey === `${organization._id}:identity-save:${selectedIdentityProviderType}`
                                    ? "Saving..."
                                    : "Save identity provider"}
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={() => handleValidateIdentityProvider(organization)}
                                  disabled={
                                    billingActionKey === `${organization._id}:identity-validate:${selectedIdentityProviderType}` ||
                                    !canValidateSelectedIdentityProvider
                                  }
                                  sx={outlinedActionButtonSx}
                                >
                                  {billingActionKey === `${organization._id}:identity-validate:${selectedIdentityProviderType}`
                                    ? "Validating..."
                                    : "Validate configuration"}
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={() =>
                                    handleStartOrganizationSsoLogin(
                                      organization,
                                      selectedIdentityProviderType,
                                    )
                                  }
                                  disabled={!canLaunchOrganizationSso}
                                  sx={outlinedActionButtonSx}
                                >
                                  Launch {selectedIdentityProviderLaunchMetadata.providerLabel} login
                                </Button>
                              </Stack>
                              {!selectedIdentityProvider ? (
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  Save the provider once before validation becomes available.
                                </Typography>
                              ) : hasUnsavedIdentityProviderDraft ? (
                                <Typography variant="caption" sx={{ color: "#ffcf82" }}>
                                  Save your draft changes before running validation.
                                </Typography>
                              ) : selectedIdentityProvider?.status !== "active" ? (
                                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.68)" }}>
                                  Set the {selectedIdentityProviderLaunchMetadata.providerLabel} provider status to Active before launching sign-in.
                                </Typography>
                              ) : null}
                            </Stack>
                          </Box>
                        </Stack>
                      ) : null}

                      {activeTab === "invites" ? (
                  <Stack spacing={2}>
                  <Box
                    component="form"
                    onSubmit={(event) => handleInviteSubmit(event, organization)}
                    sx={{
                      display: "grid",
                      gap: 1.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                      Invite a member to {organization.name}
                    </Typography>
                    <Stack
                      direction={{ xs: "column", lg: "row" }}
                      spacing={1.5}
                      useFlexGap
                      sx={{ width: "100%" }}
                    >
                      <TextField
                        label="Invite email"
                        type="email"
                        value={inviteEmails[organization._id] || ""}
                        onChange={(event) =>
                          setInviteEmails((current) => ({
                            ...current,
                            [organization._id]: event.target.value,
                          }))
                        }
                        disabled={inviteSubmittingOrgId === organization._id}
                        fullWidth
                        required
                        sx={{ flex: 1, ...lightPanelInputSx }}
                      />
                      <FormControl
                        sx={{
                          minWidth: { xs: "100%", sm: 180 },
                          width: { xs: "100%", lg: 220 },
                          ...lightPanelInputSx,
                        }}
                      >
                        <InputLabel id={`invite-role-${organization._id}`}>
                          Role
                        </InputLabel>
                        <Select
                          labelId={`invite-role-${organization._id}`}
                          value={inviteRoles[organization._id] || "member"}
                          label="Role"
                          onChange={(event) =>
                            setInviteRoles((current) => ({
                              ...current,
                              [organization._id]: event.target.value,
                            }))
                          }
                          disabled={inviteSubmittingOrgId === organization._id}
                        >
                          {INVITE_ROLE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={
                          inviteSubmittingOrgId === organization._id ||
                          !String(inviteEmails[organization._id] || "").trim()
                        }
                        sx={{ ...actionButtonSx, width: { xs: "100%", lg: "auto" } }}
                      >
                        {inviteSubmittingOrgId === organization._id
                          ? "Sending..."
                          : "Send invite"}
                      </Button>
                    </Stack>
                  </Box>

                  <Box sx={{ display: "grid", gap: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                        Invites
                      </Typography>
                      {renderHelpButton("org-invites", "Invites")}
                    </Stack>

                    {invitesLoadingByOrganizationId[organization._id] ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading invites...</Typography>
                      </Box>
                    ) : (invitesByOrganizationId[organization._id] || []).length === 0 ? (
                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                        No invites yet.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          maxHeight: 320,
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        <Stack spacing={1}>
                          {(invitesByOrganizationId[organization._id] || []).map((invite) => {
                            const inviteStatus = String(invite.status || "pending");
                            const canRevoke = inviteStatus === "pending";
                            const revokeKey = `${organization._id}:${invite._id}:revoke`;

                            return (
                              <Box
                                key={invite._id}
                                sx={{
                                  border: "1px solid rgba(255, 255, 255, 0.1)",
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 1.25,
                                  backgroundColor: "rgba(14, 24, 64, 0.82)",
                                }}
                              >
                                <Stack
                                  direction={{ xs: "column", xl: "row" }}
                                  spacing={1.5}
                                  justifyContent="space-between"
                                  alignItems={{ xs: "flex-start", md: "center" }}
                                >
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#f8fbff" }}>
                                      {invite.email}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                      {ORG_ROLE_LABELS[invite.invitedRole] || invite.invitedRole}
                                      {invite.inviter?.email ? ` · invited by ${invite.inviter.email}` : ""}
                                    </Typography>
                                  </Box>

                                  <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    useFlexGap
                                    sx={{
                                      width: { xs: "100%", xl: "auto" },
                                      flexWrap: "wrap",
                                      alignItems: { xs: "stretch", sm: "center" },
                                    }}
                                  >
                                    <Chip
                                      label={INVITE_STATUS_LABELS[inviteStatus] || inviteStatus}
                                      size="small"
                                      color={
                                        inviteStatus === "accepted"
                                          ? "success"
                                          : inviteStatus === "pending"
                                            ? "warning"
                                            : "default"
                                      }
                                      variant="outlined"
                                      sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                                    />
                                    <Button
                                      color="error"
                                      variant="outlined"
                                      onClick={() => handleRevokeInvite(organization, invite)}
                                      disabled={
                                        !canRevoke || inviteActionKey === revokeKey
                                      }
                                      sx={{
                                        ...outlinedActionButtonSx,
                                        width: { xs: "100%", sm: "auto" },
                                      }}
                                    >
                                      {inviteActionKey === revokeKey ? "Revoking..." : "Revoke"}
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                  </Stack>
                      ) : null}

                      {activeTab === "members" ? (
                  <Box sx={{ display: "grid", gap: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                        Members
                      </Typography>
                      {renderHelpButton("org-members", "Members")}
                    </Stack>

                    {membersLoadingByOrganizationId[organization._id] ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading members...</Typography>
                      </Box>
                    ) : (membersByOrganizationId[organization._id] || []).length === 0 ? (
                      <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                        No active members yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {(membersByOrganizationId[organization._id] || []).map((member) => {
                          const roleOptions =
                            MEMBER_ROLE_OPTIONS_BY_ACTOR[organization.membership?.orgRole] || [];
                          const currentUserId = currentUser?._id || currentUser?.id;
                          const canTransferOwnership =
                            organization.membership?.orgRole === "org_owner" &&
                            member.orgRole !== "org_owner";
                          const canEditDisplayedRole = roleOptions.some(
                            (option) => option.value === member.orgRole,
                          );
                          const isSelf =
                            currentUserId && member.user?._id
                              ? String(currentUserId) === String(member.user._id)
                              : false;
                          const actionsDisabled =
                            isSelf || member.orgRole === "org_owner" || !canEditDisplayedRole;
                          const showReadOnlyRole =
                            member.orgRole === "org_owner" ||
                            !canEditDisplayedRole;

                          return (
                            <Box
                              key={member._id}
                              sx={{
                                border: "1px solid rgba(255, 255, 255, 0.1)",
                                borderRadius: 2,
                                px: 1.5,
                                py: 1.25,
                                backgroundColor: "rgba(14, 24, 64, 0.82)",
                              }}
                            >
                              <Stack
                                direction={{ xs: "column", xl: "row" }}
                                spacing={1.5}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", md: "center" }}
                              >
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#f8fbff" }}>
                                    {member.user?.firstName} {member.user?.lastName}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                                    {member.user?.email}
                                  </Typography>
                                </Box>

                                <Stack
                                  direction={{ xs: "column", sm: "row" }}
                                  spacing={1}
                                  useFlexGap
                                  sx={{ width: { xs: "100%", xl: "auto" }, flexWrap: "wrap" }}
                                >
                                  {showReadOnlyRole ? (
                                    <Box
                                      sx={{
                                        minWidth: { xs: "100%", sm: 170 },
                                        width: { xs: "100%", sm: 190 },
                                        minHeight: 42,
                                        px: 1.5,
                                        borderRadius: 1,
                                        border: "1px solid rgba(255, 255, 255, 0.14)",
                                        backgroundColor: "rgba(173, 186, 228, 0.18)",
                                        display: "flex",
                                        alignItems: "center",
                                        color: "#f8fbff",
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {ORG_ROLE_LABELS[member.orgRole] || member.orgRole}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <FormControl
                                      sx={{
                                        minWidth: { xs: "100%", sm: 170 },
                                        width: { xs: "100%", sm: 190 },
                                        ...lightPanelInputSx,
                                        "& .MuiOutlinedInput-root": {
                                          ...lightPanelInputSx["& .MuiOutlinedInput-root"],
                                          minHeight: 42,
                                        },
                                        "& .MuiSelect-select": {
                                          minHeight: "42px !important",
                                          display: "flex",
                                          alignItems: "center",
                                          boxSizing: "border-box",
                                        },
                                      }}
                                      size="small"
                                    >
                                      <InputLabel id={`member-role-${member._id}`}>
                                        Role
                                      </InputLabel>
                                      <Select
                                        labelId={`member-role-${member._id}`}
                                        label="Role"
                                        value={memberRoleDrafts[member._id] || member.orgRole}
                                        onChange={(event) =>
                                          setMemberRoleDrafts((current) => ({
                                            ...current,
                                            [member._id]: event.target.value,
                                          }))
                                        }
                                        disabled={actionsDisabled || !roleOptions.length}
                                      >
                                        {roleOptions.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}

                                  <Button
                                    variant="contained"
                                    onClick={() => handleMemberRoleChange(organization, member)}
                                    disabled={
                                      actionsDisabled ||
                                      memberActionKey === `${organization._id}:${member._id}:role` ||
                                      (memberRoleDrafts[member._id] || member.orgRole) === member.orgRole
                                    }
                                    sx={{
                                      ...actionButtonSx,
                                      width: { xs: "100%", sm: "auto" },
                                    }}
                                  >
                                    {memberActionKey === `${organization._id}:${member._id}:role`
                                      ? "Saving..."
                                      : "Save"}
                                  </Button>
                                  <Button
                                    color="error"
                                    variant="outlined"
                                    onClick={() => handleRemoveMember(organization, member)}
                                    disabled={
                                      actionsDisabled ||
                                      memberActionKey === `${organization._id}:${member._id}:remove`
                                    }
                                    sx={{
                                      ...outlinedActionButtonSx,
                                      width: { xs: "100%", sm: "auto" },
                                    }}
                                  >
                                    {memberActionKey === `${organization._id}:${member._id}:remove`
                                      ? "Removing..."
                                      : "Remove"}
                                  </Button>
                                  {canTransferOwnership ? (
                                    <Button
                                      variant="outlined"
                                      onClick={() => handleOpenTransferDialog(organization, member)}
                                      disabled={
                                        memberActionKey ===
                                        `${organization._id}:${member._id}:transfer`
                                      }
                                      sx={{
                                        ...outlinedActionButtonSx,
                                        ...ownershipActionSlotSx,
                                      }}
                                    >
                                      {memberActionKey ===
                                      `${organization._id}:${member._id}:transfer`
                                        ? "Transferring..."
                                        : "Transfer ownership"}
                                    </Button>
                                  ) : organization.membership?.orgRole === "org_owner" ? (
                                    <Box sx={ownershipActionSlotSx} />
                                  ) : null}
                                </Stack>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                      ) : null}
                    </Box>
                  </Box>

                  {/* Danger zone — owner-only, and deliberately outside the tab strip so it
                      is not something you land on while doing routine admin. The server is
                      the authority on every guard here; the copy below mirrors it so the
                      owner learns the requirement before hitting a 409. */}
                  {organization.membership?.orgRole === "org_owner" ? (
                    (() => {
                      const currentUserId = currentUser?._id || currentUser?.id;
                      const loadedMembers =
                        membersByOrganizationId[organization._id] || [];
                      // Only meaningful once the member list has loaded; until then leave
                      // the count null and let the server decide.
                      const otherActiveMembers = loadedMembers.length
                        ? loadedMembers.filter(
                            (member) =>
                              !(
                                currentUserId &&
                                member.user?._id &&
                                String(member.user._id) === String(currentUserId)
                              ),
                          ).length
                        : null;
                      const blockedByMembers = otherActiveMembers > 0;

                      return (
                        <Box
                          sx={{
                            borderRadius: 2,
                            border: "1px solid rgba(248, 113, 113, 0.34)",
                            backgroundColor: "rgba(80, 22, 22, 0.28)",
                            px: { xs: 1.5, sm: 2 },
                            py: { xs: 1.5, sm: 1.75 },
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#fecaca",
                              fontWeight: 800,
                              fontSize: "0.95rem",
                              mb: 0.75,
                            }}
                          >
                            Delete organization
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "rgba(254, 226, 226, 0.86)", mb: 1.5 }}
                          >
                            Permanently deletes {organization.name}, every project it owns,
                            and all of their scenarios and results. Any active subscription
                            is cancelled. This happens immediately and cannot be undone.
                          </Typography>

                          {blockedByMembers ? (
                            <Alert severity="warning" sx={{ mb: 1.5 }}>
                              Remove the other {otherActiveMembers} member
                              {otherActiveMembers === 1 ? "" : "s"} first — deleting the
                              organization would revoke their access to everything it owns.
                            </Alert>
                          ) : null}

                          <Button
                            variant="outlined"
                            color="error"
                            disabled={
                              blockedByMembers ||
                              deletingOrganizationId === organization._id
                            }
                            onClick={() =>
                              handleOpenDeleteOrgDialog(organization, otherActiveMembers)
                            }
                            sx={{
                              minHeight: 42,
                              fontWeight: 700,
                              borderColor: "rgba(248, 113, 113, 0.6)",
                              color: "#fecaca",
                              "&:hover": {
                                borderColor: "#f87171",
                                backgroundColor: "rgba(248, 113, 113, 0.1)",
                              },
                              "&.Mui-disabled": {
                                borderColor: "rgba(255, 255, 255, 0.12)",
                                color: "rgba(241, 246, 255, 0.5)",
                              },
                            }}
                          >
                            Delete this organization
                          </Button>
                        </Box>
                      );
                    })()
                  ) : null}
                </Stack>
              ) : null}
              </Collapse>
            </Box>
              );
            })()
          ))}
        </Stack>
      )}

      <Dialog
        open={Boolean(transferDialog)}
        onClose={handleCloseTransferDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            background:
              "linear-gradient(180deg, rgba(20, 28, 68, 0.98) 0%, rgba(26, 35, 82, 0.99) 100%)",
            color: "#f5f7ff",
            border: "1px solid rgba(255, 255, 255, 0.12)",
          },
        }}
      >
        <DialogTitle>Transfer ownership</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.84)" }}>
              Transfer ownership of {transferDialog?.organizationName || "this organization"} to {" "}
              <strong>{transferDialog?.memberEmail || transferDialog?.memberName}</strong>.
              You will become an Org admin after this change.
            </Typography>
            <Alert severity="warning" sx={{ alignItems: "center" }}>
              This action changes the organization owner immediately and cannot be done from the normal role dropdown.
            </Alert>
            <TextField
              label={`Type ${OWNERSHIP_TRANSFER_CONFIRMATION} to confirm`}
              value={transferConfirmationText}
              onChange={(event) => setTransferConfirmationText(event.target.value)}
              disabled={memberActionKey.endsWith(":transfer")}
              fullWidth
              sx={darkInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseTransferDialog}
            disabled={memberActionKey.endsWith(":transfer")}
            sx={outlinedActionButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTransferOwnership}
            variant="contained"
            disabled={
              memberActionKey.endsWith(":transfer") ||
              transferConfirmationText.trim() !== OWNERSHIP_TRANSFER_CONFIRMATION
            }
            sx={actionButtonSx}
          >
            {memberActionKey.endsWith(":transfer") ? "Transferring..." : "Confirm transfer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteOrgDialog)}
        onClose={handleCloseDeleteOrgDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#111a33",
            color: "#f5f7ff",
            border: "1px solid rgba(248, 113, 113, 0.28)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#fecaca" }}>Delete organization</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="error">
              This is immediate and cannot be undone.
            </Alert>
            <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.84)" }}>
              Deleting <strong>{deleteOrgDialog?.organizationName}</strong> removes every
              project the organization owns, along with all of their scenarios, layers and
              stored results. Any active subscription is cancelled first.
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.84)" }}>
              Billing history and audit records are kept, and personal projects belonging to
              members are not affected.
            </Typography>
            {/* Server-side refusals (other members still active, or a run still on a compute
                box). Shown here so the owner can fix it and retry without losing the dialog. */}
            {deleteOrgBlocker ? (
              <Alert severity="warning">{deleteOrgBlocker}</Alert>
            ) : null}
            <TextField
              label={`Type ${ORGANIZATION_DELETE_CONFIRMATION} to confirm`}
              value={deleteOrgConfirmationText}
              onChange={(event) => setDeleteOrgConfirmationText(event.target.value)}
              disabled={Boolean(deletingOrganizationId)}
              fullWidth
              sx={darkInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDeleteOrgDialog}
            disabled={Boolean(deletingOrganizationId)}
            sx={outlinedActionButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrganization}
            variant="contained"
            color="error"
            disabled={
              Boolean(deletingOrganizationId) ||
              deleteOrgConfirmationText.trim() !== ORGANIZATION_DELETE_CONFIRMATION
            }
            sx={{ minHeight: 42, fontWeight: 700 }}
          >
            {deletingOrganizationId ? "Deleting..." : "Delete organization permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contextual help. Non-modal and draggable (same panel the project setup pages use),
          so it can stay open while the form underneath is filled in. Each section's "?" sets
          the topic; the panel links out to the full guide rather than duplicating it. */}
      <FloatingHelpPanel
        open={Boolean(helpTopic)}
        title={helpTopic ? helpTopic.title : ""}
        onClose={() => setHelpTopicKey(null)}
      >
        {helpTopic ? (
          <Stack spacing={1.25}>
            {/* The panel body is white (FloatingHelpPanel sets bgcolor #fff), while the app
                theme's default text is near-white — so the colour MUST be set explicitly here
                or the text is invisible. Same convention the setup pages use. */}
            {helpTopic.body.map((paragraph) => (
              <Typography
                key={paragraph}
                variant="body2"
                sx={{ lineHeight: 1.6, color: "#334155" }}
              >
                {paragraph}
              </Typography>
            ))}
            <Typography variant="caption" sx={{ color: "#64748b", pt: 0.5 }}>
              {helpTopic.guide ? (
                <>
                  Full details in the User Guide, section &ldquo;{helpTopic.guide}&rdquo;:{" "}
                </>
              ) : (
                <>More in the User Guide: </>
              )}
              <Link
                href={USER_GUIDE_PATH}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#1d4ed8", fontWeight: 600 }}
              >
                open the guide
              </Link>
            </Typography>
          </Stack>
        ) : null}
      </FloatingHelpPanel>
    </Box>
  );
};

export default OrganizationsTab;
